import React, { useEffect, useState } from 'react';
import { ShowContextProvider, ShowView, SimpleShowLayout, useCreatePath, useGetIdentity, useNotify, useRedirect, useShowController } from 'react-admin';
import { Box, Card, CardContent, Typography, Button, Dialog, DialogContent, DialogTitle, DialogActions, Chip, TextField, MenuItem, Alert, Tab, Tabs, Checkbox, FormControlLabel } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ordersProxy, usersProxy } from './apiGateway';
import { AdStatusType, KNOWN_AD_STATUSES, getAdStatusMeta, normalizeAdStatus } from './adStatuses';
import { LatestUserOrderInternal, LatestUserOrdersInternalResponse, UserOrderRelationType } from './types/orders';
import { getProgressOrderStatusMeta } from './progressOrderStatusMeta';
import { getPlatformUserShowUrl } from './platformUserShowLink';
import { UserAvatar } from './UserAvatar';
import { UserAvatarDeletionHistory } from './UserAvatarDeletionHistory';

type RegisteredUser = {
    id: string;
    phone: string;
    username?: string | null;
    nickname: string;
    email?: string | null;
    avatarUrl?: string | null;
    avatarId?: string | null;
    bio?: string | null;
    gender: 'MALE' | 'FEMALE' | 'UNKNOWN';
    birthday?: string | null;
    creationDate: string;
    lastUpdateDate: string;
    startDeletionDate?: string | null;
    lastOnlineDate: string;
    isBlocked: boolean;
};

type InternalUserAdPreviewResponse = {
    id: string;
    imageUrl?: string | null;
    brand: string;
    model: string;
    price: number;
    status: AdStatusType;
    lastUpdateDate: string;
};

type CursorResponse = {
    response?: InternalUserAdPreviewResponse[];
    cursor?: string | null;
};

type InternalUserAdsStatusCountItem = {
    status: AdStatusType;
    count: number | string;
};

type InternalUserAdsStatusCountResponse = {
    userId: string;
    items?: InternalUserAdsStatusCountItem[];
};

type UserAdsStatusCountViewItem = {
    status: AdStatusType;
    count: number;
};

type FeeTypeKind = 'WELCOME_BONUS' | 'FIXED' | 'DYNAMIC' | string;
type SupportedFeeType = 'WELCOME_BONUS' | 'FIXED' | 'DYNAMIC';

type SetUserFeeTypeRequest =
    | { feeType: 'WELCOME_BONUS'; feePercent: number; untilDate: string }
    | { feeType: 'FIXED'; feePercent: number; untilDate?: string | null }
    | { feeType: 'DYNAMIC' };

type InternalUserFeeInfoResponse = {
    id: string;
    userId: string;
    type: {
        type: FeeTypeKind;
        feePercent: number;
        untilDate?: string | null;
        lastFeePercent?: number | null;
        period?: {
            from: string;
            to: string;
        } | null;
        formulaVersion?: number | null;
        componentValues?: {
            sales: number;
            orders: number;
            reviews: number;
            rating: number;
        } | null;
    };
    createdAt: string;
    updatedAt: string;
};

type InternalUserBonusesResponse = {
    bonuses: number | string;
};

type UserBonusesOperationType = 'ACCRUAL' | 'WRITE_OFF';
type ChangeUserBonusesOperationType = UserBonusesOperationType;
type UserBonusesHistoryOperationType = UserBonusesOperationType | string;

type UserBonusesHistoryItemResponse = {
    title: string;
    operationType: UserBonusesHistoryOperationType;
    bonuses: number | string;
    dateTime: string;
    deeplink?: string | null;
};

type UserBonusesHistoryCursorResponse = {
    response?: UserBonusesHistoryItemResponse[];
    cursor?: string | null;
};

type UserBlockHistoryItem = {
    id?: string;
    userId: string;
    isBlocked: boolean;
    action: 'BLOCK' | 'UNBLOCK' | string;
    comment: string;
    createdAt?: string | null;
    moderator?: {
        id?: string;
        name?: string;
        login?: string;
        role?: string;
    } | null;
};

type InternalReplaceSpamMessagesResponse = {
    count: number | string;
};

type DeletedUserReviewEventItem = {
    id?: string;
    reviewId: string;
    userId: string;
    reviewType: UserReviewsDirectionType | string;
    reason: string;
    content?: {
        text?: string | null;
        gradeValue?: number | null;
        orderId?: string | null;
    } | null;
    deletedAt?: string | null;
    moderator?: {
        id?: string;
        name?: string;
        login?: string;
        role?: string;
    } | null;
};

type UserReviewsDirectionType = 'TO_USER' | 'FROM_USER';

type UserReviewReplyUserInfo = {
    id?: string | null;
    name?: string | null;
    nickname?: string | null;
    username?: string | null;
    login?: string | null;
};

type UserReviewReplyInfo = {
    text?: string | null;
    createdAt?: string | null;
    toUser?: UserReviewReplyUserInfo | null;
};

type UserReviewInternalItemResponse = {
    id: string;
    gradeValue: number;
    text?: string | null;
    toUserReply?: UserReviewReplyInfo | null;
    photosId?: string[] | null;
    date: string;
    state: string;
    counterPartyUser?: {
        id: string;
        name: string;
        isActive: boolean;
        avatarUrl?: string | null;
    } | null;
    deal?: {
        orderId: string;
        brand: string;
        model: string;
        cancelled?: {
            wasBought: boolean;
            dealResult: 'MEETING' | 'ITEM_SENT' | 'CHAT_WITH_SELLER' | 'NO_COMMUNICATION' | string;
        } | null;
    } | null;
};

type UserReviewsInternalResponse = {
    response?: UserReviewInternalItemResponse[];
    cursor?: string | null;
};

type UserReviewsStatsInternalResponse = {
    rating: number;
    reviewsToUserCount: number;
    reviewsFromUserCount: number;
    gradesCount?: {
        five: number;
        four: number;
        three: number;
        two: number;
        one: number;
    } | null;
};

type UserReviewsViewState = {
    items: UserReviewInternalItemResponse[];
    cursor: string | null;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    loadedUserId: string | null;
};

type UserReviewsStatsViewState = {
    data: UserReviewsStatsInternalResponse | null;
    loading: boolean;
    error: string | null;
    loadedUserId: string | null;
};

type UserPayoutInternalItemResponse = {
    id: string;
    ypmnId: string;
    state: string;
    amountSeller: number;
    orderId: string;
    adTitleInfo: string;
    payoutBlocked: boolean;
    finishedAt?: string | null;
    paidAt?: string | null;
    payoutErrorState?: string | null;
    payoutMethod?: string | null;
};

type UserPayoutsInternalResponse = {
    response?: UserPayoutInternalItemResponse[];
    cursor?: string | null;
};

type UserPayoutsViewState = {
    items: UserPayoutInternalItemResponse[];
    cursor: string | null;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    loadedUserId: string | null;
};

type UserLinkedPaymentMethodInternalItemResponse = {
    id: string;
    paymentMethod?: {
        type?: string;
        lastFourDigits?: string;
        expirationDate?: string;
        tokenExpirationDate?: string;
        paymentSystemName?: string;
        cardIssuerBank?: string | null;
    } | null;
    linkedDate?: string;
    paymentSystemType?: string;
    isPayoutDefaultMethod?: boolean;
    ypmnPaymentId?: string;
    status?: string;
};

type UserLinkedPaymentMethodsViewState = {
    items: UserLinkedPaymentMethodInternalItemResponse[];
    loading: boolean;
    error: string | null;
    loadedUserId: string | null;
};

type UserPayoutBlockEventItem = {
    id?: string;
    payoutId: string;
    orderId: string;
    blocked: boolean;
    comment: string;
    createdAt?: string | null;
    moderator?: {
        id?: string;
        name?: string;
        login?: string;
        role?: string;
    } | null;
};

type UserPayoutBlockHistoryViewState = {
    items: UserPayoutBlockEventItem[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
    expanded: boolean;
};

type UserManualPayoutEventItem = {
    id?: string;
    payoutId: string;
    orderId: string;
    success: boolean;
    errorMessage?: string | null;
    createdAt?: string | null;
    moderator?: {
        id?: string;
        name?: string;
        login?: string;
        role?: string;
    } | null;
};

type UserManualPayoutHistoryViewState = {
    items: UserManualPayoutEventItem[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
    expanded: boolean;
};

type UserManualPayoutLatestAttemptViewState = {
    lastAttemptAt: string | null;
    latestAttemptItem: UserManualPayoutEventItem | null;
    loading: boolean;
    error: string | null;
    loaded: boolean;
};

const FEE_INFO_NOT_SET_MESSAGE = 'Пользователь еще не выкладывал объявление и тип комиссии еще не был установлен';
const FEE_EDIT_ROLES = new Set(['moderator-admin', 'manager-admin']);
const BONUS_VIEW_ROLES = new Set(['manager-admin', 'moderator', 'moderator-admin']);
const BONUS_MANAGE_ROLES = new Set(['manager-admin', 'moderator-admin']);
const SPAM_MESSAGES_MANAGE_ROLES = new Set(['manager-admin', 'moderator-admin']);
const USER_REVIEW_REPLY_DELETE_ROLES = new Set(['moderator', 'moderator-admin', 'manager-admin']);
const BONUS_MAX_ACCRUAL = 30000;
const BONUS_HISTORY_PAGE_LIMIT = 30;
const USER_SHOW_ONLY_ADS_ROLES = new Set(['expert', 'expert-admin', 'expert-outstaff', 'expert-admin-outstaff']);

type UserLatestOrdersViewState = {
    orders: LatestUserOrderInternal[];
    hasNextOrders: boolean;
    lastOrderSerialNumber: string | null;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    loadedUserId: string | null;
};

const USER_ORDERS_PAGE_LIMIT = 30;
const USER_ORDERS_RELATION_TYPES: UserOrderRelationType[] = ['buying', 'selling'];
const USER_ORDER_RELATION_LABELS: Record<UserOrderRelationType, string> = {
    buying: 'Покупатель',
    selling: 'Продавец'
};
const USER_REVIEWS_PAGE_LIMIT = 50;
const USER_PAYOUTS_PAGE_LIMIT = 30;
const USER_PAYOUT_BLOCK_HISTORY_PAGE_LIMIT = 100;
const USER_MANUAL_PAYOUT_HISTORY_PAGE_LIMIT = 100;
const USER_PAYOUT_BLOCK_REASON_MAX_LENGTH = 250;
const USER_REVIEW_DELETE_REASON_MAX_LENGTH = 250;
const USER_DELETED_REVIEWS_PAGE_LIMIT = 100;
const MANUAL_PAYOUT_MIN_DELAY_MS = 24 * 60 * 60 * 1000;
const MANUAL_PAYOUT_ATTEMPT_COOLDOWN_MS = 15 * 60 * 1000;
const USER_REVIEW_DIRECTION_TYPES: UserReviewsDirectionType[] = ['TO_USER', 'FROM_USER'];
const USER_REVIEW_DIRECTION_LABELS: Record<UserReviewsDirectionType, string> = {
    TO_USER: 'Отзывы на этого пользователя',
    FROM_USER: 'Отзывы от этого пользователя'
};

const createInitialUserReviewsViewState = (): UserReviewsViewState => ({
    items: [],
    cursor: null,
    loading: false,
    loadingMore: false,
    error: null,
    loadedUserId: null
});

const createInitialUserReviewsState = (): Record<UserReviewsDirectionType, UserReviewsViewState> => ({
    TO_USER: createInitialUserReviewsViewState(),
    FROM_USER: createInitialUserReviewsViewState()
});

const createInitialUserReviewsStatsViewState = (): UserReviewsStatsViewState => ({
    data: null,
    loading: false,
    error: null,
    loadedUserId: null
});

const createInitialUserPayoutsViewState = (): UserPayoutsViewState => ({
    items: [],
    cursor: null,
    loading: false,
    loadingMore: false,
    error: null,
    loadedUserId: null
});

const createInitialUserLinkedPaymentMethodsViewState = (): UserLinkedPaymentMethodsViewState => ({
    items: [],
    loading: false,
    error: null,
    loadedUserId: null
});

const createInitialUserPayoutBlockHistoryViewState = (): UserPayoutBlockHistoryViewState => ({
    items: [],
    loading: false,
    error: null,
    loaded: false,
    expanded: false
});

const createInitialUserManualPayoutHistoryViewState = (): UserManualPayoutHistoryViewState => ({
    items: [],
    loading: false,
    error: null,
    loaded: false,
    expanded: false
});

const createInitialUserManualPayoutLatestAttemptViewState = (): UserManualPayoutLatestAttemptViewState => ({
    lastAttemptAt: null,
    latestAttemptItem: null,
    loading: false,
    error: null,
    loaded: false
});

const createInitialUserLatestOrdersViewState = (): UserLatestOrdersViewState => ({
    orders: [],
    hasNextOrders: false,
    lastOrderSerialNumber: null,
    loading: false,
    loadingMore: false,
    error: null,
    loadedUserId: null
});

const createInitialUserLatestOrdersState = (): Record<UserOrderRelationType, UserLatestOrdersViewState> => ({
    buying: createInitialUserLatestOrdersViewState(),
    selling: createInitialUserLatestOrdersViewState()
});

const getUserLatestOrderSerialNumber = (order?: LatestUserOrderInternal | null): string | null => {
    if (!order || typeof order !== 'object') {
        return null;
    }

    const serialNumber = (order as { serialNumber?: unknown }).serialNumber;
    const orderSerialNumber = (order as { orderSerialNumber?: unknown }).orderSerialNumber;
    const resolvedValue = serialNumber ?? orderSerialNumber;

    if (typeof resolvedValue === 'number' && Number.isFinite(resolvedValue)) {
        return String(Math.trunc(resolvedValue));
    }
    if (typeof resolvedValue === 'string') {
        const normalizedValue = resolvedValue.trim();
        return normalizedValue || null;
    }
    return null;
};

const getUserLatestOrderId = (order?: LatestUserOrderInternal | null): string | null => {
    if (!order || typeof order !== 'object') {
        return null;
    }

    const orderId = (order as { orderId?: unknown }).orderId;
    const id = (order as { id?: unknown }).id;
    const resolvedValue = orderId ?? id;

    if (typeof resolvedValue !== 'string') {
        return null;
    }

    const normalizedValue = resolvedValue.trim();
    return normalizedValue || null;
};

const getUserLatestOrderKey = (order: LatestUserOrderInternal) => {
    const orderId = getUserLatestOrderId(order);
    if (orderId) {
        return `id:${orderId}`;
    }

    const serialNumber = getUserLatestOrderSerialNumber(order);
    if (serialNumber) {
        return `sn:${serialNumber}`;
    }
    return [
        order.creationDate || '',
        order.status || '',
        order.brand || '',
        order.model || '',
        String(order.totalPrice ?? '')
    ].join('|');
};

const mergeUserLatestOrders = (prevOrders: LatestUserOrderInternal[], nextOrders: LatestUserOrderInternal[]) => {
    const mergedOrders = [...prevOrders];
    const seenKeys = new Set(prevOrders.map((order) => getUserLatestOrderKey(order)));

    nextOrders.forEach((order) => {
        const orderKey = getUserLatestOrderKey(order);
        if (!seenKeys.has(orderKey)) {
            mergedOrders.push(order);
            seenKeys.add(orderKey);
        }
    });

    return mergedOrders;
};

const mergeUserReviews = (
    prevItems: UserReviewInternalItemResponse[],
    nextItems: UserReviewInternalItemResponse[]
) => {
    const mergedItems = [...prevItems];
    const existingIds = new Set(prevItems.map((item) => item.id));

    nextItems.forEach((item) => {
        const reviewId = typeof item?.id === 'string' ? item.id : '';
        if (!reviewId || existingIds.has(reviewId)) {
            return;
        }
        mergedItems.push(item);
        existingIds.add(reviewId);
    });

    return mergedItems;
};

const mergeUserPayouts = (
    prevItems: UserPayoutInternalItemResponse[],
    nextItems: UserPayoutInternalItemResponse[]
) => {
    const mergedItems = [...prevItems];
    const existingKeys = new Set(
        prevItems.map((item) => {
            const payoutInternalId = typeof item?.id === 'string' ? item.id.trim() : '';
            if (payoutInternalId) {
                return `id:${payoutInternalId}`;
            }

            const payoutId = typeof item?.ypmnId === 'string' ? item.ypmnId.trim() : '';
            if (payoutId) {
                return `ypmn:${payoutId}`;
            }

            const orderId = typeof item?.orderId === 'string' ? item.orderId.trim() : '';
            return `fallback:${orderId}|${item?.state || ''}|${item?.finishedAt || ''}|${item?.paidAt || ''}`;
        })
    );

    nextItems.forEach((item) => {
        const payoutInternalId = typeof item?.id === 'string' ? item.id.trim() : '';
        const payoutId = typeof item?.ypmnId === 'string' ? item.ypmnId.trim() : '';
        const orderId = typeof item?.orderId === 'string' ? item.orderId.trim() : '';
        const itemKey = payoutInternalId
            ? `id:${payoutInternalId}`
            : (payoutId
                ? `ypmn:${payoutId}`
                : `fallback:${orderId}|${item?.state || ''}|${item?.finishedAt || ''}|${item?.paidAt || ''}`);

        if (existingKeys.has(itemKey)) {
            return;
        }

        mergedItems.push(item);
        existingKeys.add(itemKey);
    });

    return mergedItems;
};

const formatGender = (gender?: RegisteredUser['gender']) => {
    if (gender === 'MALE') {
        return 'Мужской';
    }
    if (gender === 'FEMALE') {
        return 'Женский';
    }
    return 'Не указан';
};

const formatReviewDirectionType = (reviewType?: string | null) => {
    const normalizedType = typeof reviewType === 'string' ? reviewType.trim().toUpperCase() : '';
    if (normalizedType === 'TO_USER') {
        return USER_REVIEW_DIRECTION_LABELS.TO_USER;
    }
    if (normalizedType === 'FROM_USER') {
        return USER_REVIEW_DIRECTION_LABELS.FROM_USER;
    }
    return 'Тип не указан';
};

const formatPayoutState = (state?: string | null) => {
    const normalizedState = typeof state === 'string' ? state.trim().toUpperCase() : '';
    if (normalizedState === 'CREATED') {
        return 'Создана';
    }
    if (normalizedState === 'REFUND') {
        return 'Возврат';
    }
    if (normalizedState === 'REFUND_ERROR') {
        return 'Ошибка возврата';
    }
    if (normalizedState === 'FINISHED') {
        return 'Заказ завершен, ожидается выплата';
    }
    if (normalizedState === 'PENDING_PAYOUT') {
        return 'Запрос на выплату';
    }
    if (normalizedState === 'PAID') {
        return 'Выплачено';
    }
    if (normalizedState === 'PAID_ERROR') {
        return 'Ошибка выплаты';
    }
    return formatValue(state ?? undefined);
};

const formatPayoutErrorState = (errorState?: string | null) => {
    const normalizedErrorState = typeof errorState === 'string' ? errorState.trim().toUpperCase() : '';
    if (!normalizedErrorState) {
        return null;
    }
    if (normalizedErrorState === 'PAYOUT_CARD_NOT_FOUND') {
        return 'Не найдена привязанная карта для выплаты';
    }
    if (normalizedErrorState === 'PAYOUT_CARD_INACTIVE') {
        return 'Привязанная карта для выплаты просрочена';
    }
    return formatValue(errorState ?? undefined);
};

const canBlockPayoutByState = (state?: string | null) => {
    const normalizedState = typeof state === 'string' ? state.trim().toUpperCase() : '';
    return normalizedState === 'PENDING_PAYOUT' || normalizedState === 'PAID_ERROR';
};

const canManualPayoutByState = (state?: string | null) => {
    const normalizedState = typeof state === 'string' ? state.trim().toUpperCase() : '';
    return normalizedState === 'PENDING_PAYOUT' || normalizedState === 'PAID_ERROR';
};

const canManualPayoutByFinishedAt = (finishedAt?: string | null) => {
    if (!finishedAt) {
        return false;
    }

    const finishedAtDate = new Date(finishedAt);
    if (Number.isNaN(finishedAtDate.getTime())) {
        return false;
    }

    return Date.now() - finishedAtDate.getTime() > MANUAL_PAYOUT_MIN_DELAY_MS;
};

const isManualPayoutAttemptCooldownActive = (lastAttemptAt?: string | null) => {
    if (!lastAttemptAt) {
        return false;
    }

    const lastAttemptDate = new Date(lastAttemptAt);
    if (Number.isNaN(lastAttemptDate.getTime())) {
        return false;
    }

    return Date.now() - lastAttemptDate.getTime() < MANUAL_PAYOUT_ATTEMPT_COOLDOWN_MS;
};

const extractApiErrorPayload = (payload: unknown): string => {
    if (!payload || typeof payload !== 'object') {
        return '';
    }

    const normalizedPayload = payload as {
        error?: unknown;
        message?: unknown;
        body?: unknown;
        response?: unknown;
        details?: unknown;
    };

    const directError = typeof normalizedPayload.error === 'string' ? normalizedPayload.error.trim() : '';
    if (directError) {
        return directError;
    }

    const directMessage = typeof normalizedPayload.message === 'string' ? normalizedPayload.message.trim() : '';
    if (directMessage) {
        return directMessage;
    }

    const nestedError = extractApiErrorPayload(
        normalizedPayload.body ?? normalizedPayload.response ?? normalizedPayload.details
    );
    return nestedError;
};

const extractApiErrorMessage = (error: unknown, fallback: string) => {
    const fallbackMessage = fallback.trim() || 'Не удалось выполнить запрос';
    if (!error || typeof error !== 'object') {
        return fallbackMessage;
    }

    const rawMessage = typeof (error as { message?: unknown }).message === 'string'
        ? (error as { message: string }).message.trim()
        : '';
    if (!rawMessage) {
        return fallbackMessage;
    }

    const separatorIndex = rawMessage.indexOf('|');
    if (separatorIndex >= 0) {
        const rawPayload = rawMessage.slice(separatorIndex + 1).trim();
        if (rawPayload) {
            try {
                const parsedPayload = JSON.parse(rawPayload);
                const payloadMessage = extractApiErrorPayload(parsedPayload);
                if (payloadMessage) {
                    return payloadMessage;
                }
            } catch (_error) {
                return rawPayload;
            }
        }
    }

    const payloadMessage = extractApiErrorPayload(error);
    if (payloadMessage && payloadMessage !== rawMessage) {
        return payloadMessage;
    }

    return fallbackMessage;
};

const formatCancelledDealResult = (dealResult?: string | null) => {
    const normalized = typeof dealResult === 'string' ? dealResult.trim().toUpperCase() : '';
    if (normalized === 'MEETING') {
        return 'Была встреча с продавцом';
    }
    if (normalized === 'ITEM_SENT') {
        return 'Вещь была отправлена';
    }
    if (normalized === 'CHAT_WITH_SELLER') {
        return 'Было общение с продавцом';
    }
    if (normalized === 'NO_COMMUNICATION') {
        return 'Общения с продавцом не было';
    }
    return formatValue(dealResult || undefined);
};

const formatValue = (value?: string | null) => {
    if (!value) {
        return 'Не указано';
    }
    return value;
};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return 'Не указано';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};

const formatPrice = (value?: number | null) => {
    if (!Number.isFinite(value)) {
        return 'Не указано';
    }
    return `${new Intl.NumberFormat('ru-RU').format(Number(value))} ₽`;
};

const normalizeCursor = (cursor?: string | null) => {
    if (typeof cursor !== 'string') {
        return null;
    }
    const normalized = cursor.trim();
    return normalized || null;
};

const normalizeCount = (value: unknown) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return 0;
    }
    return Math.trunc(parsed);
};

const normalizeFiniteNumber = (value: unknown, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const buildStatusCountItems = (items?: InternalUserAdsStatusCountItem[]): UserAdsStatusCountViewItem[] => {
    const countByStatus = new Map<string, number>();

    (Array.isArray(items) ? items : []).forEach((item) => {
        const normalizedStatus = normalizeAdStatus(item?.status);
        if (!normalizedStatus) {
            return;
        }
        const currentCount = countByStatus.get(normalizedStatus) || 0;
        countByStatus.set(normalizedStatus, currentCount + normalizeCount(item?.count));
    });

    const knownStatusItems = KNOWN_AD_STATUSES.map((status) => ({
        status,
        count: countByStatus.get(status) || 0,
    }));

    const unknownStatusItems = Array.from(countByStatus.entries())
        .filter(([status]) => !KNOWN_AD_STATUSES.includes(status as AdStatusType))
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => a.status.localeCompare(b.status));

    return [...knownStatusItems, ...unknownStatusItems];
};

const formatFeeTypeLabel = (type?: FeeTypeKind | null) => {
    const normalizedType = typeof type === 'string' ? type.trim().toUpperCase() : '';
    if (normalizedType === 'WELCOME_BONUS') {
        return 'Welcome bonus';
    }
    if (normalizedType === 'FIXED') {
        return 'Фиксированная';
    }
    if (normalizedType === 'DYNAMIC') {
        return 'Динамическая';
    }
    return formatValue(normalizedType);
};

const formatPercent = (value?: number | null) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return 'Не указано';
    }
    return `${parsed}%`;
};

const toDateTimeLocalValue = (value?: string | null) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const normalizeUntilDatePayload = (value: string) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
        return null;
    }

    const date = new Date(normalizedValue);
    if (!Number.isNaN(date.getTime())) {
        return date.toISOString();
    }

    return normalizedValue;
};

const isPastDateTimeByMinute = (value?: string | null) => {
    if (!value) {
        return false;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return false;
    }

    const now = new Date();
    now.setSeconds(0, 0);
    return date.getTime() < now.getTime();
};

const buildFeePresetDateTime = ({ days = 0, months = 0 }: { days?: number; months?: number }) => {
    const date = new Date();
    date.setSeconds(0, 0);
    if (months > 0) {
        date.setMonth(date.getMonth() + months);
    }
    if (days > 0) {
        date.setDate(date.getDate() + days);
    }
    return toDateTimeLocalValue(date.toISOString());
};

const normalizeFeeType = (type?: FeeTypeKind | null) => {
    if (typeof type !== 'string') {
        return '';
    }
    return type.trim().toUpperCase();
};

const formatCoefficientValue = (value?: number | null) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return 'Не указано';
    }
    return parsed.toFixed(2);
};

const formatBonusesHistoryOperationType = (operationType?: UserBonusesHistoryOperationType | null) => {
    const normalizedOperationType = typeof operationType === 'string' ? operationType.trim().toUpperCase() : '';
    if (normalizedOperationType === 'ACCRUAL') {
        return 'Начисление';
    }
    if (normalizedOperationType === 'WRITE_OFF') {
        return 'Списание';
    }
    return formatValue(typeof operationType === 'string' ? operationType : undefined);
};

const getBonusesHistoryOperationColor = (operationType?: UserBonusesHistoryOperationType | null) => {
    const normalizedOperationType = typeof operationType === 'string' ? operationType.trim().toUpperCase() : '';
    if (normalizedOperationType === 'ACCRUAL') {
        return 'success.main';
    }
    if (normalizedOperationType === 'WRITE_OFF') {
        return 'error.main';
    }
    return 'text.secondary';
};

const ProfileRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 2, py: 1 }}>
        <Typography color="text.secondary">{label}</Typography>
        <Typography>{value}</Typography>
    </Box>
);

export const UsersShow = () => {
    const notify = useNotify();
    const controller = useShowController<RegisteredUser>({
        queryOptions: {
            // Keep the current profile mounted so failed avatar reloads can be retried.
            onError: () => notify('Ошибка при загрузке данных пользователя', { type: 'error' })
        }
    });
    const { record, isLoading, error, refetch } = controller;
    const createPath = useCreatePath();
    const redirect = useRedirect();
    const { identity } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const isAdsOnlyView = USER_SHOW_ONLY_ADS_ROLES.has(currentUser?.role ?? '');
    const canEditFee = FEE_EDIT_ROLES.has(currentUser?.role ?? '');
    const canViewBonuses = BONUS_VIEW_ROLES.has(currentUser?.role ?? '');
    const canManageBonuses = BONUS_MANAGE_ROLES.has(currentUser?.role ?? '');
    const canManageSpamMessages = SPAM_MESSAGES_MANAGE_ROLES.has(currentUser?.role ?? '');
    const canDeleteReviewReply = USER_REVIEW_REPLY_DELETE_ROLES.has(currentUser?.role ?? '');
    const canManageManualPayout = currentUser?.role === 'manager-admin';
    const [avatarHistoryRevisions, setAvatarHistoryRevisions] = useState<Record<string, number>>({});
    const [ads, setAds] = useState<InternalUserAdPreviewResponse[]>([]);
    const [adsCursor, setAdsCursor] = useState<string | null>(null);
    const [adsLoading, setAdsLoading] = useState(false);
    const [adsLoadingMore, setAdsLoadingMore] = useState(false);
    const [adsError, setAdsError] = useState<string | null>(null);
    const [adsExpanded, setAdsExpanded] = useState(false);
    const [adsLoadedUserId, setAdsLoadedUserId] = useState<string | null>(null);
    const [statusCounts, setStatusCounts] = useState<UserAdsStatusCountViewItem[]>([]);
    const [statusCountsLoading, setStatusCountsLoading] = useState(false);
    const [statusCountsError, setStatusCountsError] = useState<string | null>(null);
    const [statusCountsExpanded, setStatusCountsExpanded] = useState(false);
    const [statusCountsLoadedUserId, setStatusCountsLoadedUserId] = useState<string | null>(null);
    const [feeInfo, setFeeInfo] = useState<InternalUserFeeInfoResponse | null>(null);
    const [feeInfoLoading, setFeeInfoLoading] = useState(false);
    const [feeInfoError, setFeeInfoError] = useState<string | null>(null);
    const [feeInfoExpanded, setFeeInfoExpanded] = useState(false);
    const [feeInfoLoadedUserId, setFeeInfoLoadedUserId] = useState<string | null>(null);
    const [bonusesBalance, setBonusesBalance] = useState<number>(0);
    const [bonusesLoading, setBonusesLoading] = useState(false);
    const [bonusesError, setBonusesError] = useState<string | null>(null);
    const [bonusesExpanded, setBonusesExpanded] = useState(false);
    const [bonusesLoadedUserId, setBonusesLoadedUserId] = useState<string | null>(null);
    const [bonusesHistoryExpanded, setBonusesHistoryExpanded] = useState(false);
    const [bonusesHistoryItems, setBonusesHistoryItems] = useState<UserBonusesHistoryItemResponse[]>([]);
    const [bonusesHistoryCursor, setBonusesHistoryCursor] = useState<string | null>(null);
    const [bonusesHistoryLoading, setBonusesHistoryLoading] = useState(false);
    const [bonusesHistoryLoadingMore, setBonusesHistoryLoadingMore] = useState(false);
    const [bonusesHistoryError, setBonusesHistoryError] = useState<string | null>(null);
    const [bonusesHistoryLoadedUserId, setBonusesHistoryLoadedUserId] = useState<string | null>(null);
    const [bonusesChangeValue, setBonusesChangeValue] = useState('');
    const [bonusesActionError, setBonusesActionError] = useState<string | null>(null);
    const [bonusesSubmitting, setBonusesSubmitting] = useState(false);
    const [bonusesAccrualConfirmOpen, setBonusesAccrualConfirmOpen] = useState(false);
    const [bonusesAccrualConfirmAmount, setBonusesAccrualConfirmAmount] = useState<number | null>(null);
    const [feeDialogOpen, setFeeDialogOpen] = useState(false);
    const [feeDialogType, setFeeDialogType] = useState<SupportedFeeType>('DYNAMIC');
    const [feeDialogPercent, setFeeDialogPercent] = useState('');
    const [feeDialogUntilDate, setFeeDialogUntilDate] = useState('');
    const [feeDialogError, setFeeDialogError] = useState<string | null>(null);
    const [feeDialogSubmitting, setFeeDialogSubmitting] = useState(false);
    const [currentIsBlocked, setCurrentIsBlocked] = useState(Boolean(record?.isBlocked));
    const [blockDialogOpen, setBlockDialogOpen] = useState(false);
    const [blockDialogComment, setBlockDialogComment] = useState('');
    const [blockDialogError, setBlockDialogError] = useState<string | null>(null);
    const [blockDialogSubmitting, setBlockDialogSubmitting] = useState(false);
    const [spamMessagesDialogOpen, setSpamMessagesDialogOpen] = useState(false);
    const [spamMessagesDialogConfirmChecked, setSpamMessagesDialogConfirmChecked] = useState(false);
    const [spamMessagesDialogError, setSpamMessagesDialogError] = useState<string | null>(null);
    const [spamMessagesDialogSubmitting, setSpamMessagesDialogSubmitting] = useState(false);
    const [blockHistoryItems, setBlockHistoryItems] = useState<UserBlockHistoryItem[]>([]);
    const [blockHistoryLoading, setBlockHistoryLoading] = useState(false);
    const [blockHistoryLoadedUserId, setBlockHistoryLoadedUserId] = useState<string | null>(null);
    const [blockHistoryExpanded, setBlockHistoryExpanded] = useState(false);
    const [latestOrdersExpanded, setLatestOrdersExpanded] = useState(false);
    const [latestOrdersActiveType, setLatestOrdersActiveType] = useState<UserOrderRelationType>('buying');
    const [latestOrdersByType, setLatestOrdersByType] = useState<Record<UserOrderRelationType, UserLatestOrdersViewState>>(
        createInitialUserLatestOrdersState
    );
    const [userReviewsExpanded, setUserReviewsExpanded] = useState(false);
    const [userReviewsActiveType, setUserReviewsActiveType] = useState<UserReviewsDirectionType>('TO_USER');
    const [userReviewsByType, setUserReviewsByType] = useState<Record<UserReviewsDirectionType, UserReviewsViewState>>(
        createInitialUserReviewsState
    );
    const [userReviewsStatsState, setUserReviewsStatsState] = useState<UserReviewsStatsViewState>(
        createInitialUserReviewsStatsViewState
    );
    const [userPayoutsExpanded, setUserPayoutsExpanded] = useState(false);
    const [userPayoutsState, setUserPayoutsState] = useState<UserPayoutsViewState>(
        createInitialUserPayoutsViewState
    );
    const [userLinkedPaymentMethodsExpanded, setUserLinkedPaymentMethodsExpanded] = useState(false);
    const [userLinkedPaymentMethodsState, setUserLinkedPaymentMethodsState] = useState<UserLinkedPaymentMethodsViewState>(
        createInitialUserLinkedPaymentMethodsViewState
    );
    const [userPayoutBlockHistoryByPayoutId, setUserPayoutBlockHistoryByPayoutId] = useState<
        Record<string, UserPayoutBlockHistoryViewState>
    >({});
    const [userManualPayoutHistoryByPayoutId, setUserManualPayoutHistoryByPayoutId] = useState<
        Record<string, UserManualPayoutHistoryViewState>
    >({});
    const [userManualPayoutLatestAttemptByPayoutId, setUserManualPayoutLatestAttemptByPayoutId] = useState<
        Record<string, UserManualPayoutLatestAttemptViewState>
    >({});
    const [payoutBlockDialogOpen, setPayoutBlockDialogOpen] = useState(false);
    const [payoutBlockDialogReason, setPayoutBlockDialogReason] = useState('');
    const [payoutBlockDialogError, setPayoutBlockDialogError] = useState<string | null>(null);
    const [payoutBlockDialogSubmitting, setPayoutBlockDialogSubmitting] = useState(false);
    const [payoutBlockTarget, setPayoutBlockTarget] = useState<{
        payoutId: string;
        orderId: string;
        payoutBlocked: boolean;
    } | null>(null);
    const [manualPayoutDialogOpen, setManualPayoutDialogOpen] = useState(false);
    const [manualPayoutDialogError, setManualPayoutDialogError] = useState<string | null>(null);
    const [manualPayoutDialogSubmitting, setManualPayoutDialogSubmitting] = useState(false);
    const [manualPayoutTarget, setManualPayoutTarget] = useState<{
        payoutId: string;
        orderId: string;
        ypmnId: string;
    } | null>(null);
    const [deleteReviewDialogOpen, setDeleteReviewDialogOpen] = useState(false);
    const [deleteReviewDialogReason, setDeleteReviewDialogReason] = useState('');
    const [deleteReviewDialogError, setDeleteReviewDialogError] = useState<string | null>(null);
    const [deleteReviewDialogSubmitting, setDeleteReviewDialogSubmitting] = useState(false);
    const [deleteReviewTarget, setDeleteReviewTarget] = useState<{
        reviewId: string;
        reviewType: UserReviewsDirectionType;
        reviewText: string;
        reviewGradeValue: number | null;
        orderId: string;
    } | null>(null);
    const [deleteReviewReplyDialogOpen, setDeleteReviewReplyDialogOpen] = useState(false);
    const [deleteReviewReplyDialogError, setDeleteReviewReplyDialogError] = useState<string | null>(null);
    const [deleteReviewReplyDialogSubmitting, setDeleteReviewReplyDialogSubmitting] = useState(false);
    const [deleteReviewReplyTarget, setDeleteReviewReplyTarget] = useState<{
        reviewId: string;
        reviewType: UserReviewsDirectionType;
    } | null>(null);
    const [deletedReviewsExpanded, setDeletedReviewsExpanded] = useState(false);
    const [deletedReviewsLoading, setDeletedReviewsLoading] = useState(false);
    const [deletedReviewsError, setDeletedReviewsError] = useState<string | null>(null);
    const [deletedReviewsItems, setDeletedReviewsItems] = useState<DeletedUserReviewEventItem[]>([]);
    const [deletedReviewsLoadedUserId, setDeletedReviewsLoadedUserId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        setAds([]);
        setAdsCursor(null);
        setAdsLoading(false);
        setAdsLoadingMore(false);
        setAdsError(null);
        setAdsExpanded(false);
        setAdsLoadedUserId(null);
        setStatusCounts([]);
        setStatusCountsLoading(false);
        setStatusCountsError(null);
        setStatusCountsExpanded(false);
        setStatusCountsLoadedUserId(null);
        setFeeInfo(null);
        setFeeInfoLoading(false);
        setFeeInfoError(null);
        setFeeInfoExpanded(false);
        setFeeInfoLoadedUserId(null);
        setBonusesBalance(0);
        setBonusesLoading(false);
        setBonusesError(null);
        setBonusesExpanded(false);
        setBonusesLoadedUserId(null);
        setBonusesHistoryExpanded(false);
        setBonusesHistoryItems([]);
        setBonusesHistoryCursor(null);
        setBonusesHistoryLoading(false);
        setBonusesHistoryLoadingMore(false);
        setBonusesHistoryError(null);
        setBonusesHistoryLoadedUserId(null);
        setBonusesChangeValue('');
        setBonusesActionError(null);
        setBonusesSubmitting(false);
        setBonusesAccrualConfirmOpen(false);
        setBonusesAccrualConfirmAmount(null);
        setFeeDialogOpen(false);
        setFeeDialogType('DYNAMIC');
        setFeeDialogPercent('');
        setFeeDialogUntilDate('');
        setFeeDialogError(null);
        setFeeDialogSubmitting(false);
        setCurrentIsBlocked(Boolean(record?.isBlocked));
        setBlockDialogOpen(false);
        setBlockDialogComment('');
        setBlockDialogError(null);
        setBlockDialogSubmitting(false);
        setSpamMessagesDialogOpen(false);
        setSpamMessagesDialogConfirmChecked(false);
        setSpamMessagesDialogError(null);
        setSpamMessagesDialogSubmitting(false);
        setBlockHistoryItems([]);
        setBlockHistoryLoading(false);
        setBlockHistoryLoadedUserId(null);
        setBlockHistoryExpanded(false);
        setLatestOrdersExpanded(false);
        setLatestOrdersActiveType('buying');
        setLatestOrdersByType(createInitialUserLatestOrdersState());
        setUserReviewsExpanded(false);
        setUserReviewsActiveType('TO_USER');
        setUserReviewsByType(createInitialUserReviewsState());
        setUserReviewsStatsState(createInitialUserReviewsStatsViewState());
        setUserPayoutsExpanded(false);
        setUserPayoutsState(createInitialUserPayoutsViewState());
        setUserLinkedPaymentMethodsExpanded(false);
        setUserLinkedPaymentMethodsState(createInitialUserLinkedPaymentMethodsViewState());
        setUserPayoutBlockHistoryByPayoutId({});
        setUserManualPayoutHistoryByPayoutId({});
        setUserManualPayoutLatestAttemptByPayoutId({});
        setPayoutBlockDialogOpen(false);
        setPayoutBlockDialogReason('');
        setPayoutBlockDialogError(null);
        setPayoutBlockDialogSubmitting(false);
        setPayoutBlockTarget(null);
        setManualPayoutDialogOpen(false);
        setManualPayoutDialogError(null);
        setManualPayoutDialogSubmitting(false);
        setManualPayoutTarget(null);
        setDeleteReviewDialogOpen(false);
        setDeleteReviewDialogReason('');
        setDeleteReviewDialogError(null);
        setDeleteReviewDialogSubmitting(false);
        setDeleteReviewTarget(null);
        setDeleteReviewReplyDialogOpen(false);
        setDeleteReviewReplyDialogError(null);
        setDeleteReviewReplyDialogSubmitting(false);
        setDeleteReviewReplyTarget(null);
        setDeletedReviewsExpanded(false);
        setDeletedReviewsLoading(false);
        setDeletedReviewsError(null);
        setDeletedReviewsItems([]);
        setDeletedReviewsLoadedUserId(null);
        if (record?.id && !isAdsOnlyView) {
            setBlockHistoryLoading(true);
            usersProxy.getUserBlockHistory({ userId: record.id, limit: 100 })
                .then((response) => {
                    if (cancelled) {
                        return;
                    }
                    const items = Array.isArray(response?.items) ? response.items : [];
                    setBlockHistoryItems(items);
                    setBlockHistoryLoadedUserId(record.id);
                })
                .catch((loadBlockHistoryError) => {
                    if (cancelled) {
                        return;
                    }
                    notify('Не удалось загрузить историю блокировки пользователя', { type: 'warning' });
                    console.error('Error fetching user block history:', loadBlockHistoryError);
                })
                .finally(() => {
                    if (cancelled) {
                        return;
                    }
                    setBlockHistoryLoading(false);
                });
        }

        return () => {
            cancelled = true;
        };
    }, [record?.id, isAdsOnlyView]);

    if (isLoading) {
        return <div>Загрузка...</div>;
    }

    if (error && !record) {
        return <div>Ошибка при загрузке данных</div>;
    }

    if (!record) {
        return <div>Данные не найдены</div>;
    }

    const totalStatusCount = statusCounts.reduce((sum, item) => sum + item.count, 0);
    const feeDialogMinDateTime = toDateTimeLocalValue(new Date().toISOString());
    const normalizedFeeType = normalizeFeeType(feeInfo?.type?.type);
    const coefficientItems = feeInfo?.type?.componentValues
        ? [
            { key: 'sales', label: 'Продажи (S)', value: Number(feeInfo.type.componentValues.sales) },
            { key: 'orders', label: 'Заказы (O)', value: Number(feeInfo.type.componentValues.orders) },
            { key: 'reviews', label: 'Отзывы (R)', value: Number(feeInfo.type.componentValues.reviews) },
            { key: 'rating', label: 'Качество отзывов (Q)', value: Number(feeInfo.type.componentValues.rating) },
        ]
        : [];
    const minCoefficient = coefficientItems.length
        ? coefficientItems.reduce((min, item) => (Number.isFinite(item.value) ? Math.min(min, item.value) : min), Number.POSITIVE_INFINITY)
        : Number.POSITIVE_INFINITY;
    const activeLatestOrdersState = latestOrdersByType[latestOrdersActiveType];
    const activeUserReviewsState = userReviewsByType[userReviewsActiveType];
    const userReviewsStats = userReviewsStatsState.data;
    const reviewsToUserCount = normalizeCount(userReviewsStats?.reviewsToUserCount);
    const reviewsFromUserCount = normalizeCount(userReviewsStats?.reviewsFromUserCount);
    const userReviewsRating = normalizeFiniteNumber(userReviewsStats?.rating);
    const gradesCount = userReviewsStats?.gradesCount;
    const gradesCountItems = [
        { key: '5', label: '5★', count: normalizeCount(gradesCount?.five) },
        { key: '4', label: '4★', count: normalizeCount(gradesCount?.four) },
        { key: '3', label: '3★', count: normalizeCount(gradesCount?.three) },
        { key: '2', label: '2★', count: normalizeCount(gradesCount?.two) },
        { key: '1', label: '1★', count: normalizeCount(gradesCount?.one) },
    ];

    const loadBlockHistory = async () => {
        if (!record.id || blockHistoryLoading) {
            return;
        }

        setBlockHistoryLoading(true);
        try {
            const response = await usersProxy.getUserBlockHistory({ userId: record.id, limit: 100 });
            const items = Array.isArray(response?.items) ? response.items : [];
            setBlockHistoryItems(items);
            setBlockHistoryLoadedUserId(record.id);
        } catch (loadBlockHistoryError) {
            notify('Не удалось загрузить историю блокировки пользователя', { type: 'warning' });
            console.error('Error fetching user block history:', loadBlockHistoryError);
        } finally {
            setBlockHistoryLoading(false);
        }
    };

    const handleOpenBlockDialog = () => {
        setBlockDialogComment('');
        setBlockDialogError(null);
        setBlockDialogOpen(true);
    };

    const handleCloseBlockDialog = () => {
        if (blockDialogSubmitting) {
            return;
        }

        setBlockDialogOpen(false);
        setBlockDialogComment('');
        setBlockDialogError(null);
    };

    const handleSubmitBlockDialog = async () => {
        if (!record.id || blockDialogSubmitting) {
            return;
        }

        const normalizedComment = blockDialogComment.trim();
        if (!normalizedComment) {
            setBlockDialogError('Укажите причину');
            return;
        }

        const targetBlockedState = !currentIsBlocked;
        setBlockDialogSubmitting(true);
        setBlockDialogError(null);

        try {
            await usersProxy.changeBlockUser({
                userId: record.id,
                isBlocked: targetBlockedState,
                comment: normalizedComment
            });
            setCurrentIsBlocked(targetBlockedState);
            setBlockDialogOpen(false);
            setBlockDialogComment('');
            notify(targetBlockedState ? 'Пользователь заблокирован' : 'Пользователь разблокирован', { type: 'success' });

            if (blockHistoryLoadedUserId !== record.id) {
                setBlockHistoryLoadedUserId(null);
            }
            await loadBlockHistory();
        } catch (changeBlockError) {
            setBlockDialogError(targetBlockedState ? 'Не удалось заблокировать пользователя' : 'Не удалось разблокировать пользователя');
            notify(targetBlockedState ? 'Не удалось заблокировать пользователя' : 'Не удалось разблокировать пользователя', { type: 'warning' });
            console.error('Error changing user block state:', changeBlockError);
        } finally {
            setBlockDialogSubmitting(false);
        }
    };

    const handleToggleBlockHistory = () => {
        setBlockHistoryExpanded((prev) => !prev);
    };

    const handleOpenSpamMessagesDialog = () => {
        if (!currentIsBlocked || !canManageSpamMessages) {
            return;
        }
        setSpamMessagesDialogError(null);
        setSpamMessagesDialogConfirmChecked(false);
        setSpamMessagesDialogOpen(true);
    };

    const handleCloseSpamMessagesDialog = () => {
        if (spamMessagesDialogSubmitting) {
            return;
        }
        setSpamMessagesDialogOpen(false);
        setSpamMessagesDialogConfirmChecked(false);
        setSpamMessagesDialogError(null);
    };

    const handleSubmitReplaceSpamMessages = async () => {
        if (!record.id || spamMessagesDialogSubmitting) {
            return;
        }
        if (!currentIsBlocked) {
            setSpamMessagesDialogError('Удалять спам-сообщения можно только у заблокированного пользователя');
            return;
        }
        if (!spamMessagesDialogConfirmChecked) {
            setSpamMessagesDialogError('Подтвердите необратимость действия');
            return;
        }

        setSpamMessagesDialogSubmitting(true);
        setSpamMessagesDialogError(null);
        try {
            const response = await usersProxy.replaceSpamMessages(record.id) as InternalReplaceSpamMessagesResponse | null;
            const affectedMessagesCount = normalizeCount(response?.count);
            notify(`Спам-сообщения удалены. Затронуто сообщений: ${affectedMessagesCount}`, { type: 'success' });
            setSpamMessagesDialogOpen(false);
            setSpamMessagesDialogConfirmChecked(false);
            setSpamMessagesDialogError(null);
            if (blockHistoryLoadedUserId !== record.id) {
                setBlockHistoryLoadedUserId(null);
            }
            await loadBlockHistory();
        } catch (replaceSpamMessagesError) {
            setSpamMessagesDialogError('Не удалось удалить спам-сообщения');
            notify('Не удалось удалить спам-сообщения', { type: 'warning' });
            console.error('Error replacing spam messages:', replaceSpamMessagesError);
        } finally {
            setSpamMessagesDialogSubmitting(false);
        }
    };

    const loadFeeInfo = async () => {
        if (!record.id || feeInfoLoading) {
            return;
        }

        setFeeInfoLoading(true);
        setFeeInfoError(null);

        try {
            const data: InternalUserFeeInfoResponse = await usersProxy.getUserFeeInfo(record.id);
            setFeeInfo(data || null);
            setFeeInfoLoadedUserId(record.id);
        } catch (loadFeeInfoError) {
            const errorStatus = (loadFeeInfoError as { status?: number } | null)?.status;
            const isNotFoundFeeInfo = errorStatus === 404;
            setFeeInfo(null);
            setFeeInfoError(isNotFoundFeeInfo ? FEE_INFO_NOT_SET_MESSAGE : 'Не удалось загрузить информацию о комиссии');
            if (!isNotFoundFeeInfo) {
                notify('Не удалось загрузить информацию о комиссии', { type: 'warning' });
            }
            console.error('Error fetching user feeInfo:', loadFeeInfoError);
        } finally {
            setFeeInfoLoading(false);
        }
    };

    const handleToggleFeeInfo = () => {
        const nextExpanded = !feeInfoExpanded;
        setFeeInfoExpanded(nextExpanded);

        if (nextExpanded && feeInfoLoadedUserId !== record.id) {
            void loadFeeInfo();
        }
    };

    const loadUserBonuses = async () => {
        if (!record.id || bonusesLoading) {
            return;
        }

        setBonusesLoading(true);
        setBonusesError(null);

        try {
            const data: InternalUserBonusesResponse = await usersProxy.getUserBonuses(record.id);
            setBonusesBalance(normalizeCount(data?.bonuses));
            setBonusesLoadedUserId(record.id);
        } catch (loadUserBonusesError) {
            setBonusesBalance(0);
            setBonusesError('Не удалось загрузить баланс бонусов');
            notify('Не удалось загрузить баланс бонусов', { type: 'warning' });
            console.error('Error fetching user bonuses:', loadUserBonusesError);
        } finally {
            setBonusesLoading(false);
        }
    };

    const handleToggleBonuses = () => {
        const nextExpanded = !bonusesExpanded;
        setBonusesExpanded(nextExpanded);

        if (nextExpanded && bonusesLoadedUserId !== record.id) {
            void loadUserBonuses();
        }
    };

    const loadBonusesHistory = async (isLoadMore = false) => {
        if (!record.id) {
            return;
        }

        if (isLoadMore) {
            if (bonusesHistoryLoading || bonusesHistoryLoadingMore || !bonusesHistoryCursor) {
                return;
            }
        } else if (bonusesHistoryLoading) {
            return;
        }

        setBonusesHistoryError(null);
        if (isLoadMore) {
            setBonusesHistoryLoadingMore(true);
        } else {
            setBonusesHistoryLoading(true);
        }

        try {
            const data: UserBonusesHistoryCursorResponse = await usersProxy.getUserBonusesHistory({
                userId: record.id,
                limit: BONUS_HISTORY_PAGE_LIMIT,
                cursor: isLoadMore ? bonusesHistoryCursor : null
            });
            const nextItems = Array.isArray(data?.response) ? data.response : [];
            setBonusesHistoryItems((currentItems) => (isLoadMore ? [...currentItems, ...nextItems] : nextItems));
            setBonusesHistoryCursor(normalizeCursor(data?.cursor));
            setBonusesHistoryLoadedUserId(record.id);
        } catch (loadBonusesHistoryError) {
            setBonusesHistoryError('Не удалось загрузить историю бонусов');
            notify('Не удалось загрузить историю бонусов', { type: 'warning' });
            console.error('Error fetching user bonuses history:', loadBonusesHistoryError);
        } finally {
            if (isLoadMore) {
                setBonusesHistoryLoadingMore(false);
            } else {
                setBonusesHistoryLoading(false);
            }
        }
    };

    const handleToggleBonusesHistory = () => {
        const nextExpanded = !bonusesHistoryExpanded;
        setBonusesHistoryExpanded(nextExpanded);

        if (nextExpanded && bonusesHistoryLoadedUserId !== record.id) {
            void loadBonusesHistory();
        }
    };

    const handleLoadMoreBonusesHistory = async () => {
        await loadBonusesHistory(true);
    };

    const parseBonusesChangeValue = () => {
        const normalizedValue = bonusesChangeValue.trim();
        if (!normalizedValue) {
            return { value: null as number | null, error: 'Укажите количество бонусов' };
        }

        const parsedValue = Number(normalizedValue);
        if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
            return { value: null as number | null, error: 'Количество бонусов должно быть целым положительным числом' };
        }

        return { value: parsedValue, error: null as string | null };
    };

    const validateBonusOperation = (operationType: ChangeUserBonusesOperationType, bonuses: number) => {
        if (operationType === 'ACCRUAL') {
            if (bonuses > BONUS_MAX_ACCRUAL) {
                return `Можно начислить не более ${BONUS_MAX_ACCRUAL} бонусов`;
            }
            return null;
        }

        if (bonuses > bonusesBalance) {
            return 'Нельзя списать больше бонусов, чем есть у пользователя';
        }
        return null;
    };

    const submitBonusesOperation = async (operationType: ChangeUserBonusesOperationType, bonuses: number) => {
        if (!record.id || bonusesSubmitting || !canManageBonuses) {
            return false;
        }

        setBonusesSubmitting(true);
        setBonusesActionError(null);

        try {
            const response = await usersProxy.changeUserBonuses(record.id, {
                operationType,
                bonuses
            });
            if (!response?.success) {
                setBonusesActionError('Операция с бонусами завершилась с ошибкой');
                notify('Операция с бонусами завершилась с ошибкой', { type: 'warning' });
                return false;
            }

            notify(operationType === 'ACCRUAL' ? 'Бонусы начислены' : 'Бонусы списаны', { type: 'success' });
            setBonusesChangeValue('');
            setBonusesLoadedUserId(null);
            await loadUserBonuses();
            if (bonusesHistoryExpanded) {
                await loadBonusesHistory();
            }
            return true;
        } catch (submitBonusesError) {
            const errorMessage = extractApiErrorMessage(submitBonusesError, 'Не удалось изменить баланс бонусов');
            setBonusesActionError(errorMessage);
            notify(errorMessage, { type: 'warning' });
            console.error('Error changing user bonuses:', submitBonusesError);
            return false;
        } finally {
            setBonusesSubmitting(false);
        }
    };

    const handleBonusesChangeValueChange = (nextValue: string) => {
        setBonusesChangeValue(nextValue);
        setBonusesActionError(null);
    };

    const handleStartAccrualBonuses = () => {
        const parsed = parseBonusesChangeValue();
        if (!parsed.value) {
            setBonusesActionError(parsed.error);
            return;
        }

        const validationError = validateBonusOperation('ACCRUAL', parsed.value);
        if (validationError) {
            setBonusesActionError(validationError);
            return;
        }

        setBonusesAccrualConfirmAmount(parsed.value);
        setBonusesAccrualConfirmOpen(true);
    };

    const handleCloseBonusesAccrualConfirm = () => {
        if (bonusesSubmitting) {
            return;
        }

        setBonusesAccrualConfirmOpen(false);
        setBonusesAccrualConfirmAmount(null);
    };

    const handleConfirmAccrualBonuses = async () => {
        if (!bonusesAccrualConfirmAmount) {
            return;
        }

        const isSuccess = await submitBonusesOperation('ACCRUAL', bonusesAccrualConfirmAmount);
        if (isSuccess) {
            setBonusesAccrualConfirmOpen(false);
            setBonusesAccrualConfirmAmount(null);
        }
    };

    const handleWriteOffBonuses = async () => {
        const parsed = parseBonusesChangeValue();
        if (!parsed.value) {
            setBonusesActionError(parsed.error);
            return;
        }

        const validationError = validateBonusOperation('WRITE_OFF', parsed.value);
        if (validationError) {
            setBonusesActionError(validationError);
            return;
        }

        await submitBonusesOperation('WRITE_OFF', parsed.value);
    };

    const handleOpenFeeDialog = () => {
        if (!canEditFee) {
            return;
        }

        const resolvedType = normalizeFeeType(feeInfo?.type?.type);
        const initialType: SupportedFeeType =
            resolvedType === 'WELCOME_BONUS' || resolvedType === 'FIXED' || resolvedType === 'DYNAMIC'
                ? resolvedType
                : 'DYNAMIC';

        setFeeDialogType(initialType);
        setFeeDialogPercent(
            typeof feeInfo?.type?.feePercent === 'number' && Number.isFinite(feeInfo.type.feePercent)
                ? String(feeInfo.type.feePercent)
                : ''
        );
        setFeeDialogUntilDate(toDateTimeLocalValue(feeInfo?.type?.untilDate ?? undefined));
        setFeeDialogError(null);
        setFeeDialogOpen(true);
    };

    const handleCloseFeeDialog = () => {
        if (feeDialogSubmitting) {
            return;
        }
        setFeeDialogOpen(false);
        setFeeDialogError(null);
    };

    const handleFeeTypeChange = (nextType: SupportedFeeType) => {
        setFeeDialogType(nextType);
        setFeeDialogError(null);
    };

    const handleSubmitFeeDialog = async () => {
        if (!record.id || feeDialogSubmitting) {
            return;
        }

        const normalizedType = feeDialogType;
        let payload: SetUserFeeTypeRequest | null = null;

        if (normalizedType === 'DYNAMIC') {
            payload = { feeType: 'DYNAMIC' };
        } else {
            const feePercent = Number(feeDialogPercent);

            if (normalizedType === 'WELCOME_BONUS') {
                if (!Number.isInteger(feePercent) || feePercent < 4 || feePercent > 10) {
                    setFeeDialogError('Процент комиссии должен быть целым числом от 4 до 10');
                    return;
                }

                const untilDatePayload = normalizeUntilDatePayload(feeDialogUntilDate || '');
                if (!untilDatePayload) {
                    setFeeDialogError('Для приветственной комиссии обязательно укажите дату окончания');
                    return;
                }
                if (isPastDateTimeByMinute(feeDialogUntilDate)) {
                    setFeeDialogError('Дата окончания не может быть в прошлом');
                    return;
                }
                payload = {
                    feeType: 'WELCOME_BONUS',
                    feePercent,
                    untilDate: untilDatePayload
                };
            } else {
                if (!Number.isInteger(feePercent) || feePercent < 4 || feePercent > 10) {
                    setFeeDialogError('Для фиксированной комиссии процент должен быть целым числом от 4 до 10');
                    return;
                }
                if (feeDialogUntilDate && isPastDateTimeByMinute(feeDialogUntilDate)) {
                    setFeeDialogError('Дата окончания не может быть в прошлом');
                    return;
                }

                payload = {
                    feeType: 'FIXED',
                    feePercent,
                    untilDate: normalizeUntilDatePayload(feeDialogUntilDate || '')
                };
            }
        }

        setFeeDialogSubmitting(true);
        setFeeDialogError(null);

        try {
            await usersProxy.setUserFeeType(record.id, payload);
            notify('Комиссия успешно обновлена', { type: 'success' });
            setFeeDialogOpen(false);
            setFeeInfoLoadedUserId(null);
            if (feeInfoExpanded) {
                await loadFeeInfo();
            }
        } catch (submitError) {
            setFeeDialogError('Не удалось обновить комиссию');
            notify('Не удалось обновить комиссию', { type: 'warning' });
            console.error('Error updating user feeType:', submitError);
        } finally {
            setFeeDialogSubmitting(false);
        }
    };

    const loadStatusCounts = async () => {
        if (!record.id || statusCountsLoading) {
            return;
        }

        setStatusCountsLoading(true);
        setStatusCountsError(null);

        try {
            const data: InternalUserAdsStatusCountResponse = await usersProxy.getUserAdsStatusCount({ userId: record.id });
            setStatusCounts(buildStatusCountItems(data?.items));
            setStatusCountsLoadedUserId(record.id);
        } catch (loadStatusCountError) {
            setStatusCounts([]);
            setStatusCountsError('Не удалось загрузить количество объявлений по статусам');
            notify('Не удалось загрузить количество объявлений по статусам', { type: 'warning' });
            console.error('Error fetching user ads statusCount:', loadStatusCountError);
        } finally {
            setStatusCountsLoading(false);
        }
    };

    const handleToggleStatusCounts = () => {
        const nextExpanded = !statusCountsExpanded;
        setStatusCountsExpanded(nextExpanded);

        if (nextExpanded && statusCountsLoadedUserId !== record.id) {
            void loadStatusCounts();
        }
    };

    const loadInitialAds = async () => {
        if (!record.id || adsLoading) {
            return;
        }

        setAdsLoading(true);
        setAdsError(null);

        try {
            const data: CursorResponse = await usersProxy.getUserAds({ userId: record.id });
            const adsResponse = Array.isArray(data?.response) ? data.response : [];
            setAds(adsResponse);
            setAdsCursor(normalizeCursor(data?.cursor));
            setAdsLoadedUserId(record.id);
        } catch (loadError) {
            setAds([]);
            setAdsCursor(null);
            setAdsError('Не удалось загрузить объявления пользователя');
            notify('Не удалось загрузить объявления пользователя', { type: 'warning' });
            console.error('Error fetching user ads:', loadError);
        } finally {
            setAdsLoading(false);
        }
    };

    const handleToggleAds = () => {
        const nextExpanded = !adsExpanded;
        setAdsExpanded(nextExpanded);

        if (nextExpanded && adsLoadedUserId !== record.id) {
            void loadInitialAds();
        }
    };

    const handleLoadMoreAds = async () => {
        if (!record.id || !adsCursor || adsLoadingMore) {
            return;
        }

        setAdsLoadingMore(true);
        try {
            const data: CursorResponse = await usersProxy.getUserAds({ userId: record.id, cursor: adsCursor });
            const nextAds = Array.isArray(data?.response) ? data.response : [];
            setAds((prevAds) => {
                const existingIds = new Set(prevAds.map((item) => item.id));
                const mergedAds = [...prevAds];
                nextAds.forEach((item) => {
                    if (!existingIds.has(item.id)) {
                        mergedAds.push(item);
                    }
                });
                return mergedAds;
            });
            setAdsCursor(normalizeCursor(data?.cursor));
        } catch (loadMoreError) {
            notify('Не удалось загрузить следующую страницу объявлений', { type: 'warning' });
            console.error('Error fetching next user ads page:', loadMoreError);
        } finally {
            setAdsLoadingMore(false);
        }
    };

    const setLatestOrdersStateByType = (
        relationType: UserOrderRelationType,
        updater: (state: UserLatestOrdersViewState) => UserLatestOrdersViewState
    ) => {
        setLatestOrdersByType((prevState) => ({
            ...prevState,
            [relationType]: updater(prevState[relationType])
        }));
    };

    const loadLatestOrders = async (relationType: UserOrderRelationType, isLoadingMore = false) => {
        if (!record.id) {
            return;
        }

        const currentState = latestOrdersByType[relationType];

        if (isLoadingMore) {
            if (currentState.loading || currentState.loadingMore || !currentState.hasNextOrders) {
                return;
            }
        } else if (currentState.loading) {
            return;
        }

        setLatestOrdersStateByType(relationType, (state) => ({
            ...state,
            error: null,
            loading: !isLoadingMore,
            loadingMore: isLoadingMore
        }));

        try {
            let cursor = isLoadingMore ? currentState.lastOrderSerialNumber : null;
            if (isLoadingMore && !cursor) {
                // Latest-order previews do not include serialNumber. Resolve only the
                // last order when another page is requested, including archived orders.
                const lastOrderId = getUserLatestOrderId(currentState.orders[currentState.orders.length - 1]);
                if (!lastOrderId) {
                    throw new Error('Last user order has no ID for pagination');
                }
                const orderInfo = await ordersProxy.getOne(lastOrderId);
                cursor = getUserLatestOrderSerialNumber(orderInfo?.body ?? orderInfo);
                if (!cursor) {
                    throw new Error('Last user order has no serial number for pagination');
                }
            }

            const data: LatestUserOrdersInternalResponse = await usersProxy.getLatestUserOrders({
                userId: record.id,
                type: relationType,
                lastOrderSerialNumber: cursor,
                limit: USER_ORDERS_PAGE_LIMIT
            });

            const nextOrders = Array.isArray(data?.orders) ? data.orders : [];
            const mergedOrders = isLoadingMore ? mergeUserLatestOrders(currentState.orders, nextOrders) : nextOrders;
            const lastOrder = nextOrders[nextOrders.length - 1];
            const lastOrderSerialNumber = getUserLatestOrderSerialNumber(lastOrder);
            const hasNextOrders = Boolean(data?.hasNextOrders) && nextOrders.length > 0;

            setLatestOrdersStateByType(relationType, (state) => ({
                ...state,
                orders: mergedOrders,
                hasNextOrders,
                lastOrderSerialNumber,
                loading: false,
                loadingMore: false,
                error: null,
                loadedUserId: record.id
            }));
        } catch (loadLatestOrdersError) {
            setLatestOrdersStateByType(relationType, (state) => ({
                ...state,
                loading: false,
                loadingMore: false,
                error: isLoadingMore ? null : 'Не удалось загрузить информацию о заказах'
            }));
            notify('Не удалось загрузить информацию о заказах пользователя', { type: 'warning' });
            console.error('Error fetching latest user orders:', loadLatestOrdersError);
        }
    };

    const handleToggleLatestOrders = () => {
        const nextExpanded = !latestOrdersExpanded;
        setLatestOrdersExpanded(nextExpanded);

        if (!nextExpanded) {
            return;
        }

        const currentState = latestOrdersByType[latestOrdersActiveType];
        if (currentState.loadedUserId !== record.id) {
            void loadLatestOrders(latestOrdersActiveType);
        }
    };

    const handleLatestOrdersTypeChange = (_event: React.SyntheticEvent, nextType: string) => {
        if (nextType !== 'buying' && nextType !== 'selling') {
            return;
        }

        setLatestOrdersActiveType(nextType);

        if (!latestOrdersExpanded) {
            return;
        }

        const targetState = latestOrdersByType[nextType];
        if (targetState.loadedUserId !== record.id && !targetState.loading) {
            void loadLatestOrders(nextType);
        }
    };

    const handleLoadMoreLatestOrders = async () => {
        await loadLatestOrders(latestOrdersActiveType, true);
    };

    const handleOpenLatestOrder = (order: LatestUserOrderInternal) => {
        const orderId = getUserLatestOrderId(order);
        if (orderId) {
            redirect('show', 'orders', orderId);
            return;
        }

        const serialNumber = getUserLatestOrderSerialNumber(order);
        if (serialNumber) {
            redirect('show', 'orders', serialNumber);
            return;
        }

        notify('Не удалось открыть заказ: отсутствует ID заказа', { type: 'warning' });
    };

    const setUserReviewsStateByType = (
        reviewType: UserReviewsDirectionType,
        updater: (state: UserReviewsViewState) => UserReviewsViewState
    ) => {
        setUserReviewsByType((prevState) => ({
            ...prevState,
            [reviewType]: updater(prevState[reviewType])
        }));
    };

    const loadUserReviews = async (reviewType: UserReviewsDirectionType, isLoadingMore = false) => {
        if (!record.id) {
            return;
        }

        const currentState = userReviewsByType[reviewType];

        if (isLoadingMore) {
            if (currentState.loading || currentState.loadingMore || !currentState.cursor) {
                return;
            }
        } else if (currentState.loading) {
            return;
        }

        setUserReviewsStateByType(reviewType, (state) => ({
            ...state,
            error: null,
            loading: !isLoadingMore,
            loadingMore: isLoadingMore
        }));

        try {
            const data: UserReviewsInternalResponse = await usersProxy.getUserReviews({
                userId: record.id,
                type: reviewType,
                limit: USER_REVIEWS_PAGE_LIMIT,
                cursor: isLoadingMore ? currentState.cursor : null
            });

            const nextItems = Array.isArray(data?.response) ? data.response : [];
            const mergedItems = isLoadingMore ? mergeUserReviews(currentState.items, nextItems) : nextItems;

            setUserReviewsStateByType(reviewType, (state) => ({
                ...state,
                items: mergedItems,
                cursor: normalizeCursor(data?.cursor),
                loading: false,
                loadingMore: false,
                error: null,
                loadedUserId: record.id
            }));
        } catch (loadUserReviewsError) {
            setUserReviewsStateByType(reviewType, (state) => ({
                ...state,
                loading: false,
                loadingMore: false,
                error: 'Не удалось загрузить отзывы'
            }));
            notify('Не удалось загрузить отзывы пользователя', { type: 'warning' });
            console.error('Error fetching user reviews:', loadUserReviewsError);
        }
    };

    const loadUserReviewsStats = async () => {
        if (!record.id || userReviewsStatsState.loading) {
            return;
        }

        setUserReviewsStatsState((state) => ({
            ...state,
            loading: true,
            error: null
        }));

        try {
            const data: UserReviewsStatsInternalResponse = await usersProxy.getUserReviewsStats({
                userId: record.id
            });
            setUserReviewsStatsState({
                data: data || null,
                loading: false,
                error: null,
                loadedUserId: record.id
            });
        } catch (loadUserReviewsStatsError) {
            setUserReviewsStatsState((state) => ({
                ...state,
                loading: false,
                error: 'Не удалось загрузить статистику отзывов'
            }));
            notify('Не удалось загрузить статистику отзывов пользователя', { type: 'warning' });
            console.error('Error fetching user reviews stats:', loadUserReviewsStatsError);
        }
    };

    const handleToggleUserReviews = () => {
        const nextExpanded = !userReviewsExpanded;
        setUserReviewsExpanded(nextExpanded);

        if (!nextExpanded) {
            return;
        }

        if (userReviewsStatsState.loadedUserId !== record.id) {
            void loadUserReviewsStats();
        }

        const currentState = userReviewsByType[userReviewsActiveType];
        if (currentState.loadedUserId !== record.id) {
            void loadUserReviews(userReviewsActiveType);
        }
    };

    const handleUserReviewsTypeChange = (_event: React.SyntheticEvent, nextType: string) => {
        if (nextType !== 'TO_USER' && nextType !== 'FROM_USER') {
            return;
        }

        setUserReviewsActiveType(nextType);

        if (!userReviewsExpanded) {
            return;
        }

        const targetState = userReviewsByType[nextType];
        if (targetState.loadedUserId !== record.id && !targetState.loading) {
            void loadUserReviews(nextType);
        }
    };

    const handleLoadMoreUserReviews = async () => {
        await loadUserReviews(userReviewsActiveType, true);
    };

    const loadUserPayouts = async (isLoadingMore = false) => {
        if (!record.id) {
            return;
        }

        if (isLoadingMore) {
            if (userPayoutsState.loading || userPayoutsState.loadingMore || !userPayoutsState.cursor) {
                return;
            }
        } else if (userPayoutsState.loading) {
            return;
        }

        setUserPayoutsState((state) => ({
            ...state,
            error: null,
            loading: !isLoadingMore,
            loadingMore: isLoadingMore
        }));

        try {
            const data: UserPayoutsInternalResponse = await usersProxy.getUserPayouts({
                userId: record.id,
                limit: USER_PAYOUTS_PAGE_LIMIT,
                cursor: isLoadingMore ? userPayoutsState.cursor : null
            });

            const nextItems = Array.isArray(data?.response) ? data.response : [];
            const mergedItems = isLoadingMore ? mergeUserPayouts(userPayoutsState.items, nextItems) : nextItems;

            setUserPayoutsState((state) => ({
                ...state,
                items: mergedItems,
                cursor: normalizeCursor(data?.cursor),
                loading: false,
                loadingMore: false,
                error: null,
                loadedUserId: record.id
            }));
            preloadUserManualPayoutLatestAttempts(mergedItems);
        } catch (loadUserPayoutsError) {
            setUserPayoutsState((state) => ({
                ...state,
                loading: false,
                loadingMore: false,
                error: 'Не удалось загрузить выплаты'
            }));
            notify('Не удалось загрузить выплаты пользователя', { type: 'warning' });
            console.error('Error fetching user payouts:', loadUserPayoutsError);
        }
    };

    const handleToggleUserPayouts = () => {
        const nextExpanded = !userPayoutsExpanded;
        setUserPayoutsExpanded(nextExpanded);

        if (nextExpanded && userPayoutsState.loadedUserId !== record.id) {
            void loadUserPayouts();
        }
    };

    const handleLoadMoreUserPayouts = async () => {
        await loadUserPayouts(true);
    };

    const loadUserLinkedPaymentMethods = async () => {
        if (!record.id || userLinkedPaymentMethodsState.loading) {
            return;
        }

        setUserLinkedPaymentMethodsState((state) => ({
            ...state,
            loading: true,
            error: null
        }));

        try {
            const data = await usersProxy.getUserLinkedPaymentMethods({
                userId: record.id
            });
            const items = Array.isArray(data)
                ? data
                : (Array.isArray(data?.response) ? data.response : []);

            setUserLinkedPaymentMethodsState((state) => ({
                ...state,
                items,
                loading: false,
                error: null,
                loadedUserId: record.id
            }));
        } catch (loadUserLinkedPaymentMethodsError) {
            setUserLinkedPaymentMethodsState((state) => ({
                ...state,
                loading: false,
                error: 'Не удалось загрузить привязанные карты'
            }));
            notify('Не удалось загрузить привязанные карты пользователя', { type: 'warning' });
            console.error('Error fetching user linked payment methods:', loadUserLinkedPaymentMethodsError);
        }
    };

    const handleToggleUserLinkedPaymentMethods = () => {
        const nextExpanded = !userLinkedPaymentMethodsExpanded;
        setUserLinkedPaymentMethodsExpanded(nextExpanded);

        if (nextExpanded && userLinkedPaymentMethodsState.loadedUserId !== record.id) {
            void loadUserLinkedPaymentMethods();
        }
    };

    const setUserPayoutBlockHistoryState = (
        payoutId: string,
        updater: (state: UserPayoutBlockHistoryViewState) => UserPayoutBlockHistoryViewState
    ) => {
        setUserPayoutBlockHistoryByPayoutId((prevState) => {
            const currentState = prevState[payoutId] || createInitialUserPayoutBlockHistoryViewState();
            return {
                ...prevState,
                [payoutId]: updater(currentState)
            };
        });
    };

    const loadUserPayoutBlockHistory = async (payoutId: string, forceReload = false) => {
        const normalizedPayoutId = typeof payoutId === 'string' ? payoutId.trim() : '';
        if (!normalizedPayoutId) {
            return;
        }

        const currentState = userPayoutBlockHistoryByPayoutId[normalizedPayoutId];
        if (!forceReload && currentState?.loaded) {
            return;
        }
        if (currentState?.loading) {
            return;
        }

        setUserPayoutBlockHistoryState(normalizedPayoutId, (state) => ({
            ...state,
            loading: true,
            error: null
        }));

        try {
            const data: { items?: UserPayoutBlockEventItem[] } = await usersProxy.getUserPayoutBlockEvents({
                payoutId: normalizedPayoutId,
                limit: USER_PAYOUT_BLOCK_HISTORY_PAGE_LIMIT
            });
            const items = Array.isArray(data?.items) ? data.items : [];
            setUserPayoutBlockHistoryState(normalizedPayoutId, (state) => ({
                ...state,
                items,
                loading: false,
                error: null,
                loaded: true
            }));
        } catch (loadHistoryError) {
            setUserPayoutBlockHistoryState(normalizedPayoutId, (state) => ({
                ...state,
                loading: false,
                error: 'Не удалось загрузить историю блокировки выплаты'
            }));
            notify('Не удалось загрузить историю блокировки выплаты', { type: 'warning' });
            console.error('Error fetching payout block history:', loadHistoryError);
        }
    };

    const handleToggleUserPayoutBlockHistory = (payoutId?: string | null) => {
        const normalizedPayoutId = typeof payoutId === 'string' ? payoutId.trim() : '';
        if (!normalizedPayoutId) {
            notify('Не удалось открыть историю: отсутствует ID выплаты', { type: 'warning' });
            return;
        }

        let shouldLoadHistory = false;
        setUserPayoutBlockHistoryByPayoutId((prevState) => {
            const currentState = prevState[normalizedPayoutId] || createInitialUserPayoutBlockHistoryViewState();
            const nextExpanded = !currentState.expanded;
            shouldLoadHistory = nextExpanded && !currentState.loaded && !currentState.loading;

            return {
                ...prevState,
                [normalizedPayoutId]: {
                    ...currentState,
                    expanded: nextExpanded
                }
            };
        });

        if (shouldLoadHistory) {
            void loadUserPayoutBlockHistory(normalizedPayoutId);
        }
    };

    const setUserManualPayoutHistoryState = (
        payoutId: string,
        updater: (state: UserManualPayoutHistoryViewState) => UserManualPayoutHistoryViewState
    ) => {
        setUserManualPayoutHistoryByPayoutId((prevState) => {
            const currentState = prevState[payoutId] || createInitialUserManualPayoutHistoryViewState();
            return {
                ...prevState,
                [payoutId]: updater(currentState)
            };
        });
    };

    const setUserManualPayoutLatestAttemptState = (
        payoutId: string,
        updater: (state: UserManualPayoutLatestAttemptViewState) => UserManualPayoutLatestAttemptViewState
    ) => {
        setUserManualPayoutLatestAttemptByPayoutId((prevState) => {
            const currentState = prevState[payoutId] || createInitialUserManualPayoutLatestAttemptViewState();
            return {
                ...prevState,
                [payoutId]: updater(currentState)
            };
        });
    };

    const loadUserManualPayoutLatestAttempt = async (
        payoutId: string,
        lookupOptions?: { orderId?: string; ypmnId?: string },
        forceReload = false
    ) => {
        if (!canManageManualPayout) {
            return;
        }

        const normalizedPayoutId = typeof payoutId === 'string' ? payoutId.trim() : '';
        const normalizedOrderId = typeof lookupOptions?.orderId === 'string' ? lookupOptions.orderId.trim() : '';
        const normalizedYpmnId = typeof lookupOptions?.ypmnId === 'string' ? lookupOptions.ypmnId.trim() : '';
        if (!normalizedPayoutId) {
            return;
        }

        const currentState = userManualPayoutLatestAttemptByPayoutId[normalizedPayoutId];
        if (!forceReload && currentState?.loaded) {
            return;
        }
        if (currentState?.loading) {
            return;
        }

        setUserManualPayoutLatestAttemptState(normalizedPayoutId, (state) => ({
            ...state,
            loading: true,
            error: null
        }));

        try {
            const data: { items?: UserManualPayoutEventItem[] } = await usersProxy.getUserManualPayoutEvents({
                payoutId: normalizedPayoutId,
                orderId: normalizedOrderId || undefined,
                ypmnId: normalizedYpmnId || undefined,
                limit: 1
            });
            const items = Array.isArray(data?.items) ? data.items : [];
            const lastAttemptAt = typeof items[0]?.createdAt === 'string' ? items[0].createdAt : null;
            const latestAttemptItem = items[0] || null;
            setUserManualPayoutLatestAttemptState(normalizedPayoutId, (state) => ({
                ...state,
                lastAttemptAt,
                latestAttemptItem,
                loading: false,
                error: null,
                loaded: true
            }));
        } catch (loadLatestAttemptError) {
            setUserManualPayoutLatestAttemptState(normalizedPayoutId, (state) => ({
                ...state,
                loading: false,
                error: 'Не удалось загрузить последнюю попытку ручной выплаты'
            }));
            console.error('Error fetching manual payout latest attempt:', loadLatestAttemptError);
        }
    };

    const preloadUserManualPayoutLatestAttempts = (items: UserPayoutInternalItemResponse[]) => {
        if (!canManageManualPayout || !Array.isArray(items) || items.length === 0) {
            return;
        }

        const uniquePayoutEntries = Array.from(new Map(
            items
                .map((item) => {
                    const payoutId = typeof item?.id === 'string' ? item.id.trim() : '';
                    const orderId = typeof item?.orderId === 'string' ? item.orderId.trim() : '';
                    const ypmnId = typeof item?.ypmnId === 'string' ? item.ypmnId.trim() : '';
                    return payoutId
                        ? [payoutId, { payoutId, orderId, ypmnId }]
                        : null;
                })
                .filter((entry): entry is [string, { payoutId: string; orderId: string; ypmnId: string }] => Boolean(entry))
        ).values());

        uniquePayoutEntries.forEach((entry) => {
            void loadUserManualPayoutLatestAttempt(entry.payoutId, {
                orderId: entry.orderId || undefined,
                ypmnId: entry.ypmnId || undefined
            });
        });
    };

    const loadUserManualPayoutHistory = async (
        payoutId: string,
        lookupOptions?: { orderId?: string; ypmnId?: string },
        forceReload = false
    ) => {
        if (!canManageManualPayout) {
            return;
        }

        const normalizedPayoutId = typeof payoutId === 'string' ? payoutId.trim() : '';
        const normalizedOrderId = typeof lookupOptions?.orderId === 'string' ? lookupOptions.orderId.trim() : '';
        const normalizedYpmnId = typeof lookupOptions?.ypmnId === 'string' ? lookupOptions.ypmnId.trim() : '';
        if (!normalizedPayoutId) {
            return;
        }

        const currentState = userManualPayoutHistoryByPayoutId[normalizedPayoutId];
        if (!forceReload && currentState?.loaded) {
            return;
        }
        if (currentState?.loading) {
            return;
        }

        setUserManualPayoutHistoryState(normalizedPayoutId, (state) => ({
            ...state,
            loading: true,
            error: null
        }));

        try {
            const data: { items?: UserManualPayoutEventItem[] } = await usersProxy.getUserManualPayoutEvents({
                payoutId: normalizedPayoutId,
                orderId: normalizedOrderId || undefined,
                ypmnId: normalizedYpmnId || undefined,
                limit: USER_MANUAL_PAYOUT_HISTORY_PAGE_LIMIT
            });
            const items = Array.isArray(data?.items) ? data.items : [];
            const latestAttemptState = userManualPayoutLatestAttemptByPayoutId[normalizedPayoutId]
                || createInitialUserManualPayoutLatestAttemptViewState();
            const fallbackLatestItem = latestAttemptState.latestAttemptItem;
            const historyItems = items.length > 0
                ? items
                : (fallbackLatestItem ? [fallbackLatestItem] : []);
            const lastAttemptAt = typeof historyItems[0]?.createdAt === 'string' ? historyItems[0].createdAt : null;
            setUserManualPayoutHistoryState(normalizedPayoutId, (state) => ({
                ...state,
                items: historyItems,
                loading: false,
                error: null,
                loaded: true
            }));
            setUserManualPayoutLatestAttemptState(normalizedPayoutId, (state) => ({
                ...state,
                lastAttemptAt,
                latestAttemptItem: historyItems[0] || state.latestAttemptItem || null,
                loading: false,
                error: null,
                loaded: true
            }));
        } catch (loadHistoryError) {
            setUserManualPayoutHistoryState(normalizedPayoutId, (state) => ({
                ...state,
                loading: false,
                error: 'Не удалось загрузить историю ручных выплат'
            }));
            notify('Не удалось загрузить историю ручных выплат', { type: 'warning' });
            console.error('Error fetching manual payout history:', loadHistoryError);
        }
    };

    const handleToggleUserManualPayoutHistory = (params?: { payoutId?: string | null; orderId?: string | null; ypmnId?: string | null }) => {
        if (!canManageManualPayout) {
            return;
        }

        const normalizedPayoutId = typeof params?.payoutId === 'string' ? params.payoutId.trim() : '';
        const normalizedOrderId = typeof params?.orderId === 'string' ? params.orderId.trim() : '';
        const normalizedYpmnId = typeof params?.ypmnId === 'string' ? params.ypmnId.trim() : '';
        if (!normalizedPayoutId) {
            notify('Не удалось открыть историю: отсутствует ID выплаты', { type: 'warning' });
            return;
        }

        let shouldLoadHistory = false;
        setUserManualPayoutHistoryByPayoutId((prevState) => {
            const currentState = prevState[normalizedPayoutId] || createInitialUserManualPayoutHistoryViewState();
            const nextExpanded = !currentState.expanded;
            shouldLoadHistory = nextExpanded && !currentState.loaded && !currentState.loading;

            return {
                ...prevState,
                [normalizedPayoutId]: {
                    ...currentState,
                    expanded: nextExpanded
                }
            };
        });

        if (shouldLoadHistory) {
            void loadUserManualPayoutHistory(
                normalizedPayoutId,
                {
                    orderId: normalizedOrderId || undefined,
                    ypmnId: normalizedYpmnId || undefined
                }
            );
        }
    };

    const handleOpenPayoutBlockDialog = (payout: UserPayoutInternalItemResponse) => {
        const normalizedOrderId = typeof payout?.orderId === 'string' ? payout.orderId.trim() : '';
        const normalizedPayoutId = typeof payout?.id === 'string' ? payout.id.trim() : '';
        const isAlreadyBlocked = Boolean(payout?.payoutBlocked);
        const isBlockingAllowedByState = canBlockPayoutByState(payout?.state);

        if (!normalizedOrderId) {
            notify('Не удалось изменить статус выплаты: отсутствует ID заказа', { type: 'warning' });
            return;
        }
        if (!normalizedPayoutId) {
            notify('Не удалось изменить статус выплаты: отсутствует ID выплаты', { type: 'warning' });
            return;
        }
        if (!isAlreadyBlocked && !isBlockingAllowedByState) {
            notify('Нельзя заблокировать выплату, которая уже прошла', { type: 'warning' });
            return;
        }

        setPayoutBlockTarget({
            payoutId: normalizedPayoutId,
            orderId: normalizedOrderId,
            payoutBlocked: Boolean(payout?.payoutBlocked)
        });
        setPayoutBlockDialogReason('');
        setPayoutBlockDialogError(null);
        setPayoutBlockDialogOpen(true);
    };

    const handleClosePayoutBlockDialog = () => {
        if (payoutBlockDialogSubmitting) {
            return;
        }

        setPayoutBlockDialogOpen(false);
        setPayoutBlockDialogReason('');
        setPayoutBlockDialogError(null);
        setPayoutBlockTarget(null);
    };

    const handlePayoutBlockReasonChange = (value: string) => {
        setPayoutBlockDialogReason(value.slice(0, USER_PAYOUT_BLOCK_REASON_MAX_LENGTH));
        if (payoutBlockDialogError) {
            setPayoutBlockDialogError(null);
        }
    };

    const handleSubmitPayoutBlockDialog = async () => {
        if (!payoutBlockTarget || payoutBlockDialogSubmitting) {
            return;
        }

        const reason = payoutBlockDialogReason.trim();
        if (!reason) {
            setPayoutBlockDialogError('Укажите причину');
            return;
        }
        if (reason.length > USER_PAYOUT_BLOCK_REASON_MAX_LENGTH) {
            setPayoutBlockDialogError(`Причина должна быть не длиннее ${USER_PAYOUT_BLOCK_REASON_MAX_LENGTH} символов`);
            return;
        }

        const nextBlockedState = !payoutBlockTarget.payoutBlocked;
        setPayoutBlockDialogSubmitting(true);
        setPayoutBlockDialogError(null);

        try {
            await usersProxy.setOrderPayoutBlocked({
                orderId: payoutBlockTarget.orderId,
                blocked: nextBlockedState,
                reason,
                payoutId: payoutBlockTarget.payoutId
            });

            setUserPayoutsState((state) => ({
                ...state,
                items: state.items.map((item) => {
                    const itemPayoutId = typeof item?.id === 'string' ? item.id.trim() : '';
                    if (itemPayoutId === payoutBlockTarget.payoutId) {
                        return {
                            ...item,
                            payoutBlocked: nextBlockedState
                        };
                    }
                    return item;
                })
            }));

            setUserPayoutBlockHistoryState(payoutBlockTarget.payoutId, (state) => ({
                ...state,
                loaded: false
            }));

            const historyState = userPayoutBlockHistoryByPayoutId[payoutBlockTarget.payoutId];
            if (historyState?.expanded) {
                await loadUserPayoutBlockHistory(payoutBlockTarget.payoutId, true);
            }

            notify(nextBlockedState ? 'Выплата заблокирована' : 'Выплата разблокирована', { type: 'success' });
            setPayoutBlockDialogOpen(false);
            setPayoutBlockDialogReason('');
            setPayoutBlockDialogError(null);
            setPayoutBlockTarget(null);
        } catch (changePayoutBlockError) {
            setPayoutBlockDialogError(nextBlockedState ? 'Не удалось заблокировать выплату' : 'Не удалось разблокировать выплату');
            notify(nextBlockedState ? 'Не удалось заблокировать выплату' : 'Не удалось разблокировать выплату', { type: 'warning' });
            console.error('Error changing payout blocked status:', changePayoutBlockError);
        } finally {
            setPayoutBlockDialogSubmitting(false);
        }
    };

    const handleOpenManualPayoutDialog = (payout: UserPayoutInternalItemResponse) => {
        if (!canManageManualPayout) {
            notify('Недостаточно прав для ручной выплаты', { type: 'warning' });
            return;
        }

        const normalizedPayoutId = typeof payout?.id === 'string' ? payout.id.trim() : '';
        const normalizedOrderId = typeof payout?.orderId === 'string' ? payout.orderId.trim() : '';
        const normalizedYpmnId = typeof payout?.ypmnId === 'string' ? payout.ypmnId.trim() : '';
        const canRunByState = canManualPayoutByState(payout?.state);
        const canRunByTime = canManualPayoutByFinishedAt(payout?.finishedAt);

        if (!normalizedPayoutId) {
            notify('Не удалось выполнить ручную выплату: отсутствует ID выплаты', { type: 'warning' });
            return;
        }
        if (!canRunByState) {
            notify('Ручная выплата доступна только для выплат в статусе PENDING_PAYOUT или PAID_ERROR', { type: 'warning' });
            return;
        }
        if (payout?.payoutBlocked) {
            notify('Нельзя выполнить ручную выплату, пока выплата заблокирована', { type: 'warning' });
            return;
        }
        if (!canRunByTime) {
            notify('Ручная выплата доступна спустя 24 часа после окончания заказа', { type: 'warning' });
            return;
        }

        setManualPayoutTarget({
            payoutId: normalizedPayoutId,
            orderId: normalizedOrderId,
            ypmnId: normalizedYpmnId
        });
        setManualPayoutDialogError(null);
        setManualPayoutDialogOpen(true);
    };

    const handleCloseManualPayoutDialog = () => {
        if (manualPayoutDialogSubmitting) {
            return;
        }

        setManualPayoutDialogOpen(false);
        setManualPayoutDialogError(null);
        setManualPayoutTarget(null);
    };

    const handleSubmitManualPayoutDialog = async () => {
        if (!manualPayoutTarget || manualPayoutDialogSubmitting) {
            return;
        }
        if (!canManageManualPayout) {
            setManualPayoutDialogError('Недостаточно прав для ручной выплаты');
            return;
        }

        const targetPayoutId = manualPayoutTarget.payoutId;
        const targetOrderId = manualPayoutTarget.orderId || undefined;
        const targetYpmnId = manualPayoutTarget.ypmnId || undefined;
        setManualPayoutDialogSubmitting(true);
        setManualPayoutDialogError(null);

        try {
            await usersProxy.manualPayout({
                payoutId: targetPayoutId,
                orderId: targetOrderId,
                ypmnId: targetYpmnId
            });

            setUserManualPayoutHistoryState(targetPayoutId, (state) => ({
                ...state,
                loaded: false
            }));

            const historyState = userManualPayoutHistoryByPayoutId[targetPayoutId];
            if (historyState?.expanded) {
                await loadUserManualPayoutHistory(
                    targetPayoutId,
                    { orderId: targetOrderId, ypmnId: targetYpmnId },
                    true
                );
            }

            notify('Ручная выплата запущена', { type: 'success' });
            setManualPayoutDialogOpen(false);
            setManualPayoutDialogError(null);
            setManualPayoutTarget(null);
            await loadUserManualPayoutLatestAttempt(
                targetPayoutId,
                { orderId: targetOrderId, ypmnId: targetYpmnId },
                true
            );
            await loadUserPayouts();
        } catch (manualPayoutError) {
            setUserManualPayoutHistoryState(targetPayoutId, (state) => ({
                ...state,
                loaded: false
            }));
            const historyState = userManualPayoutHistoryByPayoutId[targetPayoutId];
            if (historyState?.expanded) {
                await loadUserManualPayoutHistory(
                    targetPayoutId,
                    { orderId: targetOrderId, ypmnId: targetYpmnId },
                    true
                );
            }
            await loadUserManualPayoutLatestAttempt(
                targetPayoutId,
                { orderId: targetOrderId, ypmnId: targetYpmnId },
                true
            );
            const errorMessage = extractApiErrorMessage(manualPayoutError, 'Не удалось выполнить ручную выплату');
            setManualPayoutDialogError(errorMessage);
            console.error('Error processing manual payout:', manualPayoutError);
        } finally {
            setManualPayoutDialogSubmitting(false);
        }
    };

    const loadDeletedUserReviews = async () => {
        if (!record.id || deletedReviewsLoading) {
            return;
        }

        setDeletedReviewsLoading(true);
        setDeletedReviewsError(null);

        try {
            const data: { items?: DeletedUserReviewEventItem[] } = await usersProxy.getDeletedUserReviews({
                userId: record.id,
                limit: USER_DELETED_REVIEWS_PAGE_LIMIT
            });
            const items = Array.isArray(data?.items) ? data.items : [];
            setDeletedReviewsItems(items);
            setDeletedReviewsLoadedUserId(record.id);
        } catch (loadDeletedUserReviewsError) {
            setDeletedReviewsItems([]);
            setDeletedReviewsError('Не удалось загрузить удаленные отзывы');
            notify('Не удалось загрузить удаленные отзывы', { type: 'warning' });
            console.error('Error fetching deleted user reviews:', loadDeletedUserReviewsError);
        } finally {
            setDeletedReviewsLoading(false);
        }
    };

    const handleToggleDeletedUserReviews = () => {
        const nextExpanded = !deletedReviewsExpanded;
        setDeletedReviewsExpanded(nextExpanded);

        if (nextExpanded && deletedReviewsLoadedUserId !== record.id) {
            void loadDeletedUserReviews();
        }
    };

    const handleOpenDeleteReviewDialog = (
        review: UserReviewInternalItemResponse,
        reviewType: UserReviewsDirectionType
    ) => {
        const normalizedReviewId = typeof review?.id === 'string' ? review.id.trim() : '';
        if (!normalizedReviewId) {
            notify('Не удалось удалить отзыв: отсутствует ID', { type: 'warning' });
            return;
        }

        const normalizedOrderId = typeof review?.deal?.orderId === 'string'
            ? review.deal.orderId.trim()
            : '';
        const normalizedReviewText = typeof review?.text === 'string' ? review.text : '';
        const normalizedReviewGradeValue = Number.isFinite(Number(review?.gradeValue))
            ? Number(review.gradeValue)
            : null;

        setDeleteReviewTarget({
            reviewId: normalizedReviewId,
            reviewType,
            reviewText: normalizedReviewText,
            reviewGradeValue: normalizedReviewGradeValue,
            orderId: normalizedOrderId
        });
        setDeleteReviewDialogReason('');
        setDeleteReviewDialogError(null);
        setDeleteReviewDialogOpen(true);
    };

    const handleCloseDeleteReviewDialog = () => {
        if (deleteReviewDialogSubmitting) {
            return;
        }

        setDeleteReviewDialogOpen(false);
        setDeleteReviewDialogReason('');
        setDeleteReviewDialogError(null);
        setDeleteReviewTarget(null);
    };

    const handleDeleteReviewReasonChange = (value: string) => {
        setDeleteReviewDialogReason(value.slice(0, USER_REVIEW_DELETE_REASON_MAX_LENGTH));
        if (deleteReviewDialogError) {
            setDeleteReviewDialogError(null);
        }
    };

    const handleSubmitDeleteReview = async () => {
        if (!deleteReviewTarget || deleteReviewDialogSubmitting) {
            return;
        }

        const reason = deleteReviewDialogReason.trim();
        if (!reason) {
            setDeleteReviewDialogError('Укажите комментарий для пользователя');
            return;
        }
        if (reason.length > USER_REVIEW_DELETE_REASON_MAX_LENGTH) {
            setDeleteReviewDialogError(`Комментарий должен быть не длиннее ${USER_REVIEW_DELETE_REASON_MAX_LENGTH} символов`);
            return;
        }

        setDeleteReviewDialogSubmitting(true);
        setDeleteReviewDialogError(null);

        try {
            await usersProxy.deleteUserReview({
                reviewId: deleteReviewTarget.reviewId,
                userId: record.id,
                reviewType: deleteReviewTarget.reviewType,
                reason,
                reviewText: deleteReviewTarget.reviewText,
                reviewGradeValue: deleteReviewTarget.reviewGradeValue,
                orderId: deleteReviewTarget.orderId
            });

            notify('Отзыв удален', { type: 'success' });
            setDeleteReviewDialogOpen(false);
            setDeleteReviewDialogReason('');
            setDeleteReviewTarget(null);
            setDeletedReviewsLoadedUserId(null);

            await Promise.all([
                loadUserReviews(deleteReviewTarget.reviewType),
                loadUserReviewsStats(),
                deletedReviewsExpanded ? loadDeletedUserReviews() : Promise.resolve()
            ]);
        } catch (deleteReviewError) {
            setDeleteReviewDialogError('Не удалось удалить отзыв');
            notify('Не удалось удалить отзыв', { type: 'warning' });
            console.error('Error deleting user review:', deleteReviewError);
        } finally {
            setDeleteReviewDialogSubmitting(false);
        }
    };

    const handleOpenDeleteReviewReplyDialog = (
        review: UserReviewInternalItemResponse,
        reviewType: UserReviewsDirectionType
    ) => {
        const normalizedReviewId = typeof review?.id === 'string' ? review.id.trim() : '';
        if (!normalizedReviewId) {
            notify('Не удалось удалить ответ: отсутствует ID отзыва', { type: 'warning' });
            return;
        }

        const normalizedReplyText = typeof review?.toUserReply?.text === 'string'
            ? review.toUserReply.text.trim()
            : '';
        if (!normalizedReplyText) {
            notify('Ответ продавца для этого отзыва не найден', { type: 'warning' });
            return;
        }

        setDeleteReviewReplyTarget({
            reviewId: normalizedReviewId,
            reviewType
        });
        setDeleteReviewReplyDialogError(null);
        setDeleteReviewReplyDialogOpen(true);
    };

    const handleCloseDeleteReviewReplyDialog = () => {
        if (deleteReviewReplyDialogSubmitting) {
            return;
        }

        setDeleteReviewReplyDialogOpen(false);
        setDeleteReviewReplyDialogError(null);
        setDeleteReviewReplyTarget(null);
    };

    const handleSubmitDeleteReviewReply = async () => {
        if (!deleteReviewReplyTarget || deleteReviewReplyDialogSubmitting) {
            return;
        }

        setDeleteReviewReplyDialogSubmitting(true);
        setDeleteReviewReplyDialogError(null);

        try {
            await usersProxy.deleteUserReviewReply(deleteReviewReplyTarget.reviewId);
            notify('Ответ продавца удален', { type: 'success' });
            setDeleteReviewReplyDialogOpen(false);
            setDeleteReviewReplyTarget(null);
            await loadUserReviews(deleteReviewReplyTarget.reviewType);
        } catch (deleteReviewReplyError) {
            const errorMessage = extractApiErrorMessage(deleteReviewReplyError, 'Не удалось удалить ответ продавца');
            setDeleteReviewReplyDialogError(errorMessage);
            notify(errorMessage, { type: 'warning' });
            console.error('Error deleting review reply:', deleteReviewReplyError);
        } finally {
            setDeleteReviewReplyDialogSubmitting(false);
        }
    };

    const handleOpenOrderInNewTab = (orderId?: string | null) => {
        const normalizedOrderId = typeof orderId === 'string' ? orderId.trim() : '';
        if (!normalizedOrderId) {
            notify('Не удалось открыть заказ: отсутствует ID заказа', { type: 'warning' });
            return;
        }

        const orderPath = createPath({
            resource: 'orders',
            id: normalizedOrderId,
            type: 'show'
        });
        const normalizedOrderPath = orderPath.startsWith('/') ? orderPath : `/${orderPath}`;
        const orderUrl = `${window.location.origin}/#${normalizedOrderPath}`;
        window.open(orderUrl, '_blank', 'noopener,noreferrer');
    };

    const handleOpenReviewCounterPartyUser = (counterPartyUserId?: string | null) => {
        const normalizedUserId = typeof counterPartyUserId === 'string' ? counterPartyUserId.trim() : '';
        if (!normalizedUserId) {
            notify('Не удалось открыть пользователя: отсутствует ID', { type: 'warning' });
            return;
        }

        window.open(getPlatformUserShowUrl(normalizedUserId), '_blank', 'noopener,noreferrer');
    };

    const handleCopyUserId = async () => {
        const userId = typeof record.id === 'string' ? record.id.trim() : '';
        if (!userId) {
            return;
        }

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(userId);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = userId;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            notify('ID пользователя скопирован', { type: 'success' });
        } catch (copyError) {
            notify('Не удалось скопировать ID пользователя', { type: 'warning' });
            console.error('Error copying user id:', copyError);
        }
    };

    return (
        <ShowContextProvider value={controller}>
        <ShowView title={`Пользователь ${record.username || record.id}`}>
            <SimpleShowLayout>
                <Box sx={{ display: 'grid', gap: 2 }}>
                    {currentIsBlocked && (
                        <Alert severity="error">
                            Это заблокированный пользователь
                        </Alert>
                    )}

                    <>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                        <UserAvatar
                                            key={record.id}
                                            user={record}
                                            role={currentUser?.role}
                                            onRefresh={() => refetch({ throwOnError: true })}
                                            onDeleted={() => {
                                                notify('Фото профиля удалено', { type: 'success' });
                                                setAvatarHistoryRevisions(prev => ({ ...prev, [record.id]: (prev[record.id] || 0) + 1 }));
                                            }}
                                        />
                                        <Box sx={{ display: 'grid', gap: 1, flex: 1 }}>
                                            <Typography variant="h5" fontWeight={700}>
                                                {formatValue(record.username ?? undefined)}
                                            </Typography>
                                            <Typography color="text.secondary">
                                                {record.nickname
                                                    ? `@${record.nickname}`
                                                    : (
                                                        <Box
                                                            component="button"
                                                            type="button"
                                                            onClick={handleCopyUserId}
                                                            sx={{
                                                                border: 0,
                                                                background: 'transparent',
                                                                p: 0,
                                                                m: 0,
                                                                color: 'inherit',
                                                                cursor: 'pointer',
                                                                textDecoration: 'underline',
                                                                textDecorationStyle: 'dotted'
                                                            }}
                                                        >
                                                            {`ID: ${record.id}`}
                                                        </Box>
                                                    )}
                                            </Typography>
                                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                {formatValue(record.bio ?? undefined)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                                <Chip label={`Пол: ${formatGender(record.gender)}`} size="small" />
                                                <Chip label={currentIsBlocked ? 'Заблокирован' : 'Активен'} color={currentIsBlocked ? 'error' : 'success'} size="small" />
                                                <Chip label={`Онлайн: ${formatDateTime(record.lastOnlineDate)}`} size="small" />
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Контактная информация
                                </Typography>
                                <ProfileRow label="Телефон" value={formatValue(record.phone)} />
                                <ProfileRow label="Email" value={formatValue(record.email ?? undefined)} />
                                <ProfileRow label="Дата рождения" value={formatValue(record.birthday ?? undefined)} />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Профиль и статус
                                </Typography>
                                <ProfileRow
                                    label="ID"
                                    value={(
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={handleCopyUserId}
                                            sx={{
                                                border: 0,
                                                background: 'transparent',
                                                p: 0,
                                                m: 0,
                                                fontFamily: 'monospace',
                                                fontSize: 'inherit',
                                                color: 'primary.main',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                textDecorationStyle: 'dotted',
                                                '&:hover': { color: 'primary.dark' }
                                            }}
                                        >
                                            {record.id}
                                        </Box>
                                    )}
                                />
                                <ProfileRow label="Юзернейм" value={formatValue(record.username ?? undefined)} />
                                <ProfileRow label="Никнейм" value={record.nickname ? `@${record.nickname}` : 'Не указано'} />
                                <ProfileRow label="Пол" value={formatGender(record.gender)} />
                                <ProfileRow label="Заблокирован" value={currentIsBlocked ? 'Да' : 'Нет'} />
                            </CardContent>
                        </Card>
                    </Box>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Служебная информация
                            </Typography>
                            <ProfileRow label="Дата создания" value={formatDateTime(record.creationDate)} />
                            <ProfileRow label="Последнее обновление" value={formatDateTime(record.lastUpdateDate)} />
                            <ProfileRow label="Последний онлайн" value={formatDateTime(record.lastOnlineDate)} />
                            <ProfileRow label="Дата удаления" value={formatDateTime(record.startDeletionDate ?? undefined)} />
                        </CardContent>
                    </Card>

                    {!isAdsOnlyView && (
                        <>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={handleToggleFeeInfo}
                                    aria-expanded={feeInfoExpanded}
                                    aria-label={feeInfoExpanded ? 'Свернуть информацию о комиссии' : 'Развернуть информацию о комиссии'}
                                    sx={{
                                        flex: 1,
                                        minWidth: 240,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        border: 0,
                                        background: 'transparent',
                                        p: 0,
                                        m: 0,
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Typography variant="h6">Информация о комиссии</Typography>
                                    {feeInfoExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </Box>
                            </Box>

                            {feeInfoExpanded && (
                                <Box sx={{ mt: 1.5 }}>
                                    {canEditFee && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                                            <Button variant="outlined" size="small" onClick={handleOpenFeeDialog}>
                                                Изменить комиссию
                                            </Button>
                                        </Box>
                                    )}

                                    {feeInfoLoading ? (
                                        <Typography color="text.secondary">Загрузка данных...</Typography>
                                    ) : feeInfoError ? (
                                        <Typography color={feeInfoError === FEE_INFO_NOT_SET_MESSAGE ? 'text.secondary' : 'error'}>
                                            {feeInfoError}
                                        </Typography>
                                    ) : !feeInfo ? (
                                        <Typography color="text.secondary">Информация о комиссии не найдена</Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                                <Typography color="text.secondary" variant="body2">
                                                    Тип комиссии
                                                </Typography>
                                                <Typography variant="h6" fontWeight={700}>
                                                    {formatFeeTypeLabel(feeInfo.type?.type)}
                                                </Typography>
                                            </Box>
                                            {(normalizedFeeType === 'WELCOME_BONUS' || normalizedFeeType === 'FIXED') && (
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gap: 1.25,
                                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
                                                    }}
                                                >
                                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                        <Typography color="text.secondary" variant="body2">
                                                            Процент комиссии
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={700}>
                                                            {formatPercent(feeInfo.type?.feePercent)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                        <Typography color="text.secondary" variant="body2">
                                                            Действует до
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={700}>
                                                            {normalizedFeeType === 'FIXED' && !feeInfo.type?.untilDate
                                                                ? 'Бессрочно'
                                                                : formatDateTime(feeInfo.type?.untilDate ?? undefined)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            {normalizedFeeType === 'DYNAMIC' && (
                                                <>
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gap: 1.25,
                                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }
                                                        }}
                                                    >
                                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                            <Typography color="text.secondary" variant="body2">
                                                                Текущий процент
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight={700}>
                                                                {formatPercent(feeInfo.type?.feePercent)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                            <Typography color="text.secondary" variant="body2">
                                                                Предыдущий процент
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight={700}>
                                                                {formatPercent(feeInfo.type?.lastFeePercent)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                            <Typography color="text.secondary" variant="body2">
                                                                Период
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight={700}>
                                                                {feeInfo.type?.period?.from && feeInfo.type?.period?.to
                                                                    ? `${formatDateTime(feeInfo.type.period.from)} - ${formatDateTime(feeInfo.type.period.to)}`
                                                                    : 'Не указано'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                            <Typography color="text.secondary" variant="body2">
                                                                Версия алгоритма SORQ
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight={700}>
                                                                {feeInfo.type?.formulaVersion ?? 'Не указано'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                                        Красным подсвечены показатели, которые пользователю нужно улучшить, чтобы комиссия снижалась.
                                                    </Alert>

                                                    {coefficientItems.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gap: 1.25,
                                                                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }
                                                            }}
                                                        >
                                                            {coefficientItems.map((coefficient) => {
                                                                const isLowest = Number.isFinite(minCoefficient) && coefficient.value === minCoefficient;
                                                                return (
                                                                    <Box
                                                                        key={coefficient.key}
                                                                        sx={{
                                                                            border: '1px solid',
                                                                            borderColor: isLowest ? 'error.main' : 'divider',
                                                                            bgcolor: isLowest ? 'error.50' : 'transparent',
                                                                            borderRadius: 2,
                                                                            p: 1.25
                                                                        }}
                                                                    >
                                                                        <Typography color="text.secondary" variant="body2">
                                                                            {coefficient.label}
                                                                        </Typography>
                                                                        <Typography variant="h6" fontWeight={700} color={isLowest ? 'error.main' : 'text.primary'}>
                                                                            {formatCoefficientValue(coefficient.value)}
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                </>
                                            )}

                                            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.25 }}>
                                                <Typography color="text.secondary" variant="body2">
                                                    Создано: {formatDateTime(feeInfo.createdAt)}
                                                </Typography>
                                                <Typography color="text.secondary" variant="body2">
                                                    Обновлено: {formatDateTime(feeInfo.updatedAt)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {canViewBonuses && (
                        <Card>
                            <CardContent>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={handleToggleBonuses}
                                    aria-expanded={bonusesExpanded}
                                    aria-label={bonusesExpanded ? 'Свернуть информацию о бонусах' : 'Развернуть информацию о бонусах'}
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        border: 0,
                                        background: 'transparent',
                                        p: 0,
                                        m: 0,
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Typography variant="h6">Информация о бонусах</Typography>
                                    {bonusesExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </Box>

                                {bonusesExpanded && (
                                    <Box sx={{ mt: 1.5, display: 'grid', gap: 1.5 }}>
                                        {bonusesLoading ? (
                                            <Typography color="text.secondary">Загрузка данных...</Typography>
                                        ) : bonusesError ? (
                                            <Typography color="error">{bonusesError}</Typography>
                                        ) : (
                                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                                <Typography color="text.secondary" variant="body2">
                                                    Баланс бонусов
                                                </Typography>
                                                <Typography variant="h6" fontWeight={700}>
                                                    {bonusesBalance}
                                                </Typography>
                                            </Box>
                                        )}

                                        {canManageBonuses && (
                                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, display: 'grid', gap: 1.25 }}>
                                                <Typography variant="subtitle2">
                                                    Управление бонусами
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    size="small"
                                                    label="Количество бонусов"
                                                    value={bonusesChangeValue}
                                                    onChange={(event) => handleBonusesChangeValueChange(event.target.value)}
                                                    inputProps={{ min: 1, step: 1 }}
                                                    helperText={`Начисление: максимум ${BONUS_MAX_ACCRUAL}. Списание: до текущего баланса (${bonusesBalance}).`}
                                                />
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        onClick={handleStartAccrualBonuses}
                                                        disabled={bonusesSubmitting}
                                                    >
                                                        {bonusesSubmitting ? 'Выполнение...' : 'Начислить'}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="warning"
                                                        onClick={handleWriteOffBonuses}
                                                        disabled={bonusesSubmitting || bonusesLoading || Boolean(bonusesError)}
                                                    >
                                                        {bonusesSubmitting ? 'Выполнение...' : 'Списать'}
                                                    </Button>
                                                </Box>
                                                {bonusesActionError && (
                                                    <Typography color="error" variant="body2">
                                                        {bonusesActionError}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}

                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={handleToggleBonusesHistory}
                                                aria-expanded={bonusesHistoryExpanded}
                                                aria-label={bonusesHistoryExpanded ? 'Свернуть историю списаний и начислений' : 'Развернуть историю списаний и начислений'}
                                                sx={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    border: 0,
                                                    background: 'transparent',
                                                    p: 0,
                                                    m: 0,
                                                    textAlign: 'left',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    История списаний и начислений
                                                </Typography>
                                                {bonusesHistoryExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </Box>

                                            {bonusesHistoryExpanded && (
                                                <Box sx={{ mt: 1.5, display: 'grid', gap: 1.25 }}>
                                                    {bonusesHistoryLoading ? (
                                                        <Typography color="text.secondary">Загрузка данных...</Typography>
                                                    ) : bonusesHistoryError ? (
                                                        <Typography color="error">{bonusesHistoryError}</Typography>
                                                    ) : bonusesHistoryItems.length === 0 ? (
                                                        <Typography color="text.secondary">История операций не найдена</Typography>
                                                    ) : (
                                                        <Box sx={{ display: 'grid', gap: 1 }}>
                                                            {bonusesHistoryItems.map((historyItem, index) => {
                                                                const historyBonuses = Number(historyItem?.bonuses);
                                                                const normalizedOperationType = typeof historyItem?.operationType === 'string'
                                                                    ? historyItem.operationType.trim().toUpperCase()
                                                                    : '';
                                                                const bonusesSign = normalizedOperationType === 'WRITE_OFF'
                                                                    ? '-'
                                                                    : (normalizedOperationType === 'ACCRUAL' ? '+' : '');
                                                                const bonusesLabel = Number.isFinite(historyBonuses)
                                                                    ? `${bonusesSign}${Math.abs(Math.trunc(historyBonuses))}`
                                                                    : formatValue(typeof historyItem?.bonuses === 'string' ? historyItem.bonuses : undefined);
                                                                const operationColor = getBonusesHistoryOperationColor(historyItem?.operationType);
                                                                const historyKey = `${historyItem?.dateTime || ''}-${historyItem?.title || ''}-${index}`;
                                                                return (
                                                                    <Box
                                                                        key={historyKey}
                                                                        sx={{
                                                                            border: '1px solid',
                                                                            borderColor: 'divider',
                                                                            borderRadius: 2,
                                                                            p: 1.25,
                                                                            display: 'grid',
                                                                            gap: 0.5
                                                                        }}
                                                                    >
                                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                            {formatValue(historyItem?.title)}
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: operationColor, fontWeight: 600 }}>
                                                                            Тип операции: {formatBonusesHistoryOperationType(historyItem?.operationType)}
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: operationColor, fontWeight: 600 }}>
                                                                            Бонусы: {bonusesLabel}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            Дата: {formatDateTime(historyItem?.dateTime)}
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    )}

                                                    {bonusesHistoryCursor && !bonusesHistoryError && (
                                                        <Box>
                                                            <Button variant="outlined" onClick={handleLoadMoreBonusesHistory} disabled={bonusesHistoryLoadingMore}>
                                                                {bonusesHistoryLoadingMore ? 'Загрузка...' : 'Показать еще'}
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardContent>
                            <Box
                                component="button"
                                type="button"
                                onClick={handleToggleStatusCounts}
                                aria-expanded={statusCountsExpanded}
                                aria-label={statusCountsExpanded ? 'Свернуть количество объявлений по статусам' : 'Развернуть количество объявлений по статусам'}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: 0,
                                    background: 'transparent',
                                    p: 0,
                                    m: 0,
                                    textAlign: 'left',
                                    cursor: 'pointer'
                                }}
                            >
                                <Typography variant="h6">Количество объявлений по статусам</Typography>
                                {statusCountsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>

                            {statusCountsExpanded && (
                                <Box sx={{ mt: 1.5 }}>
                                    {statusCountsLoading ? (
                                        <Typography color="text.secondary">Загрузка данных...</Typography>
                                    ) : statusCountsError ? (
                                        <Typography color="error">{statusCountsError}</Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 2,
                                                    p: 1.5
                                                }}
                                            >
                                                <Typography color="text.secondary" variant="body2">
                                                    Всего объявлений
                                                </Typography>
                                                <Typography variant="h5" fontWeight={700}>
                                                    {totalStatusCount}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: 1.25,
                                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }
                                                }}
                                            >
                                                {statusCounts.map((item) => {
                                                    const statusMeta = getAdStatusMeta(item.status);
                                                    return (
                                                        <Box
                                                            key={String(item.status)}
                                                            sx={{
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 2,
                                                                p: 1.25,
                                                                display: 'grid',
                                                                gap: 1
                                                            }}
                                                        >
                                                            <Chip label={statusMeta.label} color={statusMeta.color} size="small" sx={{ justifySelf: 'start' }} />
                                                            <Typography variant="h6" fontWeight={700}>
                                                                {item.count}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                        </>
                    )}
                    </>

                    <Card>
                        <CardContent>
                            <Box
                                component="button"
                                type="button"
                                onClick={handleToggleAds}
                                aria-expanded={adsExpanded}
                                aria-label={adsExpanded ? 'Свернуть объявления пользователя' : 'Развернуть объявления пользователя'}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: 0,
                                    background: 'transparent',
                                    p: 0,
                                    m: 0,
                                    textAlign: 'left',
                                    cursor: 'pointer'
                                }}
                            >
                                <Typography variant="h6">Объявления пользователя</Typography>
                                {adsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>

                            {adsExpanded && (
                                <Box sx={{ mt: 1.5 }}>
                                    {adsLoading ? (
                                        <Typography color="text.secondary">Загрузка объявлений...</Typography>
                                    ) : adsError ? (
                                        <Typography color="error">{adsError}</Typography>
                                    ) : ads.length === 0 ? (
                                        <Typography color="text.secondary">Объявления пользователя не найдены</Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                                            {ads.map((ad) => {
                                                const statusMeta = getAdStatusMeta(ad.status);
                                                return (
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        key={ad.id}
                                                        onClick={() => redirect('show', 'userAds', ad.id)}
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: { xs: '1fr', sm: '80px 1fr auto' },
                                                            gap: 1.5,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 2,
                                                            p: 1.5,
                                                            alignItems: 'center',
                                                            width: '100%',
                                                            background: 'transparent',
                                                            textAlign: 'left',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                borderColor: 'primary.main',
                                                                bgcolor: 'action.hover',
                                                            },
                                                            '&:focus-visible': {
                                                                outline: '2px solid',
                                                                outlineColor: 'primary.main',
                                                                outlineOffset: 2,
                                                            }
                                                        }}
                                                    >
                                                        {ad.imageUrl ? (
                                                            <Box
                                                                component="img"
                                                                src={ad.imageUrl}
                                                                alt={`${ad.brand} ${ad.model}`}
                                                                sx={{ width: 64, height: 64, borderRadius: 1.5, objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: 64,
                                                                    height: 64,
                                                                    borderRadius: 1.5,
                                                                    bgcolor: 'action.hover',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'text.secondary',
                                                                    fontSize: 12
                                                                }}
                                                            >
                                                                Без фото
                                                            </Box>
                                                        )}

                                                        <Box sx={{ display: 'grid', gap: 0.5 }}>
                                                            <Typography fontWeight={600}>
                                                                {formatValue(ad.brand)} {formatValue(ad.model)}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                ID: {ad.id}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Обновлено: {formatDateTime(ad.lastUpdateDate)}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'grid', gap: 0.75, justifyItems: { xs: 'start', sm: 'end' } }}>
                                                            <Typography fontWeight={700}>{formatPrice(ad.price)}</Typography>
                                                            <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    {adsCursor && (
                                        <Box sx={{ mt: 2 }}>
                                            <Button variant="outlined" onClick={handleLoadMoreAds} disabled={adsLoadingMore}>
                                                {adsLoadingMore ? 'Загрузка...' : 'Показать еще'}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {!isAdsOnlyView && (
                        <>
                            <Card>
                                <CardContent>
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={handleToggleLatestOrders}
                                        aria-expanded={latestOrdersExpanded}
                                        aria-label={latestOrdersExpanded ? 'Свернуть информацию о заказах пользователя' : 'Развернуть информацию о заказах пользователя'}
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            border: 0,
                                            background: 'transparent',
                                            p: 0,
                                            m: 0,
                                            textAlign: 'left',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Typography variant="h6">Информация о заказах</Typography>
                                        {latestOrdersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </Box>

                                    {latestOrdersExpanded && (
                                        <Box sx={{ mt: 1.5 }}>
                                            <Tabs
                                                value={latestOrdersActiveType}
                                                onChange={handleLatestOrdersTypeChange}
                                                variant="fullWidth"
                                                sx={{ mb: 1.5 }}
                                            >
                                                {USER_ORDERS_RELATION_TYPES.map((relationType) => (
                                                    <Tab
                                                        key={relationType}
                                                        value={relationType}
                                                        label={USER_ORDER_RELATION_LABELS[relationType]}
                                                    />
                                                ))}
                                            </Tabs>

                                    {activeLatestOrdersState.loading ? (
                                        <Typography color="text.secondary">Загрузка заказов...</Typography>
                                    ) : activeLatestOrdersState.error ? (
                                        <Typography color="error">{activeLatestOrdersState.error}</Typography>
                                    ) : activeLatestOrdersState.orders.length === 0 ? (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                            Заказов нет
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.25 }}>
                                            {activeLatestOrdersState.orders.map((order, index) => {
                                                const statusMeta = getProgressOrderStatusMeta(order.status);
                                                const statusLabel = statusMeta?.label || formatValue(
                                                    typeof order.status === 'string' ? order.status : undefined
                                                );
                                                const serialNumber = getUserLatestOrderSerialNumber(order);
                                                const orderKey = serialNumber ? `latest-order-${serialNumber}` : `${getUserLatestOrderKey(order)}-${index}`;

                                                return (
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        key={orderKey}
                                                        onClick={() => handleOpenLatestOrder(order)}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 2,
                                                            p: 1.25,
                                                            display: 'grid',
                                                            gap: 0.75,
                                                            width: '100%',
                                                            background: 'transparent',
                                                            textAlign: 'left',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                borderColor: 'primary.main',
                                                                bgcolor: 'action.hover',
                                                            },
                                                            '&:focus-visible': {
                                                                outline: '2px solid',
                                                                outlineColor: 'primary.main',
                                                                outlineOffset: 2,
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                                <Chip label={statusLabel} color={statusMeta?.color || 'default'} size="small" />
                                                                {order.isArchive === true && (
                                                                    <Chip
                                                                        label="Архивный"
                                                                        color="warning"
                                                                        variant="outlined"
                                                                        size="small"
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatDateTime(order.creationDate)}
                                                            </Typography>
                                                        </Box>
                                                        <Typography fontWeight={700}>
                                                            {formatValue(order.brand)} {formatValue(order.model)}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                            {serialNumber && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    № заказа: {serialNumber}
                                                                </Typography>
                                                            )}
                                                            <Typography fontWeight={700}>
                                                                {formatPrice(order.totalPrice)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    {activeLatestOrdersState.hasNextOrders && (
                                        <Box sx={{ mt: 2 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={handleLoadMoreLatestOrders}
                                                disabled={activeLatestOrdersState.loadingMore}
                                            >
                                                {activeLatestOrdersState.loadingMore ? 'Загрузка...' : 'Показать еще'}
                                            </Button>
                                        </Box>
                                    )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

                    <Card>
                        <CardContent>
                            <Box
                                component="button"
                                type="button"
                                onClick={handleToggleUserReviews}
                                aria-expanded={userReviewsExpanded}
                                aria-label={userReviewsExpanded ? 'Свернуть отзывы пользователя' : 'Развернуть отзывы пользователя'}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: 0,
                                    background: 'transparent',
                                    p: 0,
                                    m: 0,
                                    textAlign: 'left',
                                    cursor: 'pointer'
                                }}
                            >
                                <Typography variant="h6">Отзывы</Typography>
                                {userReviewsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>

                            {userReviewsExpanded && (
                                <Box sx={{ mt: 1.5 }}>
                                    {userReviewsStatsState.loading ? (
                                        <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                                            Загрузка статистики отзывов...
                                        </Typography>
                                    ) : userReviewsStatsState.error ? (
                                        <Typography color="error" sx={{ mb: 1.5 }}>
                                            {userReviewsStatsState.error}
                                        </Typography>
                                    ) : userReviewsStats ? (
                                        <Box sx={{ display: 'grid', gap: 1.25, mb: 1.5 }}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: 1.25,
                                                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }
                                                }}
                                            >
                                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Рейтинг
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={700}>
                                                        {userReviewsRating.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Отзывов на пользователя
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={700}>
                                                        {reviewsToUserCount}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Отзывов от пользователя
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={700}>
                                                        {reviewsFromUserCount}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ display: 'grid', gap: 0.75 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Распределение оценок
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                                    {gradesCountItems.map((item) => (
                                                        <Chip
                                                            key={item.key}
                                                            label={`${item.label}: ${item.count}`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    ) : null}

                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1.5 }}>
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={handleToggleDeletedUserReviews}
                                            aria-expanded={deletedReviewsExpanded}
                                            aria-label={deletedReviewsExpanded ? 'Свернуть удаленные отзывы' : 'Развернуть удаленные отзывы'}
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                border: 0,
                                                background: 'transparent',
                                                p: 1.25,
                                                m: 0,
                                                textAlign: 'left',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Удаленные отзывы командой
                                            </Typography>
                                            {deletedReviewsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                        </Box>

                                        {deletedReviewsExpanded && (
                                            <Box sx={{ px: 1.25, pb: 1.25 }}>
                                                {deletedReviewsLoading ? (
                                                    <Typography color="text.secondary">Загрузка удаленных отзывов...</Typography>
                                                ) : deletedReviewsError ? (
                                                    <Typography color="error">{deletedReviewsError}</Typography>
                                                ) : deletedReviewsItems.length === 0 ? (
                                                    <Typography color="text.secondary">Удаленных отзывов нет</Typography>
                                                ) : (
                                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                                        {deletedReviewsItems.map((deletedItem) => {
                                                            const moderatorName = formatValue(
                                                                deletedItem?.moderator?.name || deletedItem?.moderator?.login || undefined
                                                            );
                                                            const deletedReviewText = typeof deletedItem?.content?.text === 'string'
                                                                ? deletedItem.content.text
                                                                : '';
                                                            const deletedOrderId = typeof deletedItem?.content?.orderId === 'string'
                                                                ? deletedItem.content.orderId.trim()
                                                                : '';
                                                            const deletedReviewGradeValue = Number(deletedItem?.content?.gradeValue);
                                                            const hasDeletedReviewGradeValue = Number.isFinite(deletedReviewGradeValue);

                                                            return (
                                                                <Box
                                                                    key={deletedItem.id || `${deletedItem.reviewId}-${deletedItem.deletedAt || ''}`}
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                        borderRadius: 2,
                                                                        p: 1.25,
                                                                        display: 'grid',
                                                                        gap: 0.75
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                                            <Chip
                                                                                label={formatReviewDirectionType(deletedItem.reviewType)}
                                                                                size="small"
                                                                                variant="outlined"
                                                                            />
                                                                            <Chip
                                                                                label={`ID отзыва: ${formatValue(deletedItem.reviewId || undefined)}`}
                                                                                size="small"
                                                                                variant="outlined"
                                                                            />
                                                                        </Box>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {formatDateTime(deletedItem.deletedAt)}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                                        {formatValue(deletedItem.reason || undefined)}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Оценка отзыва: {hasDeletedReviewGradeValue ? deletedReviewGradeValue : 'Не указано'}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                        Текст отзыва: {deletedReviewText || 'Не указан'}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            ID заказа:
                                                                        </Typography>
                                                                        {deletedOrderId ? (
                                                                            <Box
                                                                                component="button"
                                                                                type="button"
                                                                                onClick={() => handleOpenOrderInNewTab(deletedOrderId)}
                                                                                sx={{
                                                                                    border: 0,
                                                                                    background: 'transparent',
                                                                                    p: 0,
                                                                                    m: 0,
                                                                                    color: 'primary.main',
                                                                                    cursor: 'pointer',
                                                                                    textDecoration: 'underline',
                                                                                    textDecorationStyle: 'dotted',
                                                                                    fontFamily: 'monospace',
                                                                                    fontSize: '0.875rem',
                                                                                    '&:hover': { color: 'primary.dark' }
                                                                                }}
                                                                            >
                                                                                {deletedOrderId}
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                Не указан
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Удалил: {moderatorName}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    <Tabs
                                        value={userReviewsActiveType}
                                        onChange={handleUserReviewsTypeChange}
                                        variant="fullWidth"
                                        sx={{ mb: 1.5 }}
                                    >
                                        {USER_REVIEW_DIRECTION_TYPES.map((reviewType) => (
                                            <Tab
                                                key={reviewType}
                                                value={reviewType}
                                                label={USER_REVIEW_DIRECTION_LABELS[reviewType]}
                                            />
                                        ))}
                                    </Tabs>

                                    {activeUserReviewsState.loading ? (
                                        <Typography color="text.secondary">Загрузка отзывов...</Typography>
                                    ) : activeUserReviewsState.error ? (
                                        <Typography color="error">{activeUserReviewsState.error}</Typography>
                                    ) : activeUserReviewsState.items.length === 0 ? (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                            Отзывов нет
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.25 }}>
                                            {activeUserReviewsState.items.map((review) => {
                                                const counterPartyUserId = typeof review?.counterPartyUser?.id === 'string'
                                                    ? review.counterPartyUser.id.trim()
                                                    : '';
                                                const dealOrderId = typeof review?.deal?.orderId === 'string'
                                                    ? review.deal.orderId.trim()
                                                    : '';
                                                const dealBrand = typeof review?.deal?.brand === 'string'
                                                    ? review.deal.brand.trim()
                                                    : '';
                                                const dealModel = typeof review?.deal?.model === 'string'
                                                    ? review.deal.model.trim()
                                                    : '';
                                                const dealCancelled = review?.deal?.cancelled || null;
                                                const dealWasBoughtLabel = dealCancelled
                                                    ? (dealCancelled.wasBought ? 'Да' : 'Нет')
                                                    : 'Не указано';
                                                const normalizedReviewState = typeof review?.state === 'string'
                                                    ? review.state.trim().toUpperCase()
                                                    : '';
                                                const reviewStateLabel = normalizedReviewState === 'PUBLISHED'
                                                    ? 'Опубликован'
                                                    : (normalizedReviewState === 'MODERATION'
                                                        ? 'На модерации'
                                                        : formatValue(review.state));
                                                const reviewText = typeof review?.text === 'string' && review.text.trim()
                                                    ? review.text
                                                    : 'Отзыв без текста';
                                                const reviewReplyText = typeof review?.toUserReply?.text === 'string'
                                                    ? review.toUserReply.text.trim()
                                                    : '';
                                                const hasReviewReply = Boolean(reviewReplyText);
                                                const reviewReplyCreatedAt = typeof review?.toUserReply?.createdAt === 'string'
                                                    ? review.toUserReply.createdAt
                                                    : '';
                                                const replyToUserId = typeof review?.toUserReply?.toUser?.id === 'string'
                                                    ? review.toUserReply.toUser.id.trim()
                                                    : '';
                                                const replyToUserNameCandidates = [
                                                    review?.toUserReply?.toUser?.name,
                                                    review?.toUserReply?.toUser?.nickname,
                                                    review?.toUserReply?.toUser?.username,
                                                    review?.toUserReply?.toUser?.login
                                                ];
                                                const replyToUserName = replyToUserNameCandidates
                                                    .map((value) => (typeof value === 'string' ? value.trim() : ''))
                                                    .find(Boolean) || '';
                                                const reviewPhotoUrls = Array.isArray(review?.photosId)
                                                    ? review.photosId
                                                        .filter((url): url is string => typeof url === 'string')
                                                        .map((url) => url.trim())
                                                        .filter(Boolean)
                                                    : [];
                                                const photosCount = Array.isArray(review?.photosId) ? review.photosId.length : 0;
                                                const photosLabel = photosCount > 0 ? `Фото: ${photosCount}` : 'Без фотографий';
                                                const isCounterPartyInactive = review?.counterPartyUser?.isActive === false;

                                                return (
                                                    <Box
                                                        key={review.id}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 2,
                                                            p: 1.25,
                                                            display: 'grid',
                                                            gap: 0.75
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                                <Chip label={`Оценка: ${review.gradeValue}`} size="small" color="info" />
                                                                <Chip label={reviewStateLabel} size="small" variant="outlined" />
                                                                <Chip label={photosLabel} size="small" variant="outlined" />
                                                            </Box>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatDateTime(review.date)}
                                                            </Typography>
                                                        </Box>

                                                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {reviewText}
                                                        </Typography>

                                                        {hasReviewReply && (
                                                            <Box
                                                                sx={{
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    borderRadius: 2,
                                                                    p: 1,
                                                                    display: 'grid',
                                                                    gap: 0.75
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Ответ продавца
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {formatDateTime(reviewReplyCreatedAt)}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                                    {reviewReplyText}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Получатель: {formatValue(replyToUserName || undefined)}
                                                                    {replyToUserId ? ` · ID: ${replyToUserId}` : ''}
                                                                </Typography>
                                                                {canDeleteReviewReply && (
                                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="error"
                                                                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                                                                            onClick={() => handleOpenDeleteReviewReplyDialog(review, userReviewsActiveType)}
                                                                            disabled={deleteReviewReplyDialogSubmitting}
                                                                        >
                                                                            Удалить ответ
                                                                        </Button>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}

                                                        {reviewPhotoUrls.length > 0 && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexWrap: 'wrap',
                                                                    gap: 0.75
                                                                }}
                                                            >
                                                                {reviewPhotoUrls.map((photoUrl, photoIndex) => (
                                                                    <Box
                                                                        key={`${review.id}-photo-${photoIndex}`}
                                                                        component="button"
                                                                        type="button"
                                                                        onClick={() => window.open(photoUrl, '_blank', 'noopener,noreferrer')}
                                                                        sx={{
                                                                            border: '1px solid',
                                                                            borderColor: 'divider',
                                                                            borderRadius: 1.5,
                                                                            p: 0,
                                                                            m: 0,
                                                                            cursor: 'pointer',
                                                                            overflow: 'hidden',
                                                                            background: 'transparent',
                                                                            width: 56,
                                                                            height: 56,
                                                                            flex: '0 0 56px',
                                                                            '&:hover': { opacity: 0.85 }
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            component="img"
                                                                            src={photoUrl}
                                                                            alt={`Фото отзыва ${photoIndex + 1}`}
                                                                            loading="lazy"
                                                                            sx={{
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                objectFit: 'cover',
                                                                                display: 'block'
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}

                                                        <Box
                                                            sx={{
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 2,
                                                                p: 1,
                                                                display: 'grid',
                                                                gap: 0.5
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Заказ
                                                                </Typography>
                                                                {dealCancelled && (
                                                                    <Chip label="Отменен" size="small" color="warning" variant="outlined" />
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    ID заказа:
                                                                </Typography>
                                                                {dealOrderId ? (
                                                                    <Box
                                                                        component="button"
                                                                        type="button"
                                                                        onClick={() => handleOpenOrderInNewTab(dealOrderId)}
                                                                        sx={{
                                                                            border: 0,
                                                                            background: 'transparent',
                                                                            p: 0,
                                                                            m: 0,
                                                                            color: 'primary.main',
                                                                            cursor: 'pointer',
                                                                            textDecoration: 'underline',
                                                                            textDecorationStyle: 'dotted',
                                                                            fontFamily: 'monospace',
                                                                            fontSize: '0.875rem',
                                                                            '&:hover': { color: 'primary.dark' }
                                                                        }}
                                                                    >
                                                                        {dealOrderId}
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Не указан
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Бренд: {formatValue(dealBrand || undefined)}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Модель: {formatValue(dealModel || undefined)}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Была куплена: {dealWasBoughtLabel}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Результат отмены: {formatCancelledDealResult(dealCancelled?.dealResult)}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Пользователь: {formatValue(review?.counterPartyUser?.name)}
                                                                {isCounterPartyInactive ? ' (пользователь удален или заблокирован)' : ''}
                                                                {counterPartyUserId ? (
                                                                    <>
                                                                        {' · '}
                                                                        <Box
                                                                            component="button"
                                                                            type="button"
                                                                            onClick={() => handleOpenReviewCounterPartyUser(counterPartyUserId)}
                                                                            sx={{
                                                                                border: 0,
                                                                                background: 'transparent',
                                                                                p: 0,
                                                                                m: 0,
                                                                                color: 'primary.main',
                                                                                cursor: 'pointer',
                                                                                textDecoration: 'underline',
                                                                                textDecorationStyle: 'dotted',
                                                                                fontFamily: 'monospace',
                                                                                fontSize: '0.875rem',
                                                                                '&:hover': { color: 'primary.dark' }
                                                                            }}
                                                                        >
                                                                            ID: {counterPartyUserId}
                                                                        </Box>
                                                                    </>
                                                                ) : (
                                                                    ' · ID: Не указан'
                                                                )}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                                                                    onClick={() => handleOpenDeleteReviewDialog(review, userReviewsActiveType)}
                                                                    disabled={deleteReviewDialogSubmitting}
                                                                >
                                                                    Удалить
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    {activeUserReviewsState.cursor && (
                                        <Box sx={{ mt: 2 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={handleLoadMoreUserReviews}
                                                disabled={activeUserReviewsState.loadingMore}
                                            >
                                                {activeUserReviewsState.loadingMore ? 'Загрузка...' : 'Показать еще'}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box
                                component="button"
                                type="button"
                                onClick={handleToggleUserPayouts}
                                aria-expanded={userPayoutsExpanded}
                                aria-label={userPayoutsExpanded ? 'Свернуть выплаты пользователя' : 'Развернуть выплаты пользователя'}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: 0,
                                    background: 'transparent',
                                    p: 0,
                                    m: 0,
                                    textAlign: 'left',
                                    cursor: 'pointer'
                                }}
                            >
                                <Typography variant="h6">Выплаты</Typography>
                                {userPayoutsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>

                            {userPayoutsExpanded && (
                                <Box sx={{ mt: 1.5 }}>
                                    {userPayoutsState.loading ? (
                                        <Typography color="text.secondary">Загрузка выплат...</Typography>
                                    ) : userPayoutsState.error ? (
                                        <Typography color="error">{userPayoutsState.error}</Typography>
                                    ) : userPayoutsState.items.length === 0 ? (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                            Выплат нет
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.25 }}>
                                            {userPayoutsState.items.map((payout, index) => {
                                                const normalizedInternalPayoutId = typeof payout?.id === 'string' ? payout.id.trim() : '';
                                                const normalizedPayoutId = typeof payout?.ypmnId === 'string' ? payout.ypmnId.trim() : '';
                                                const normalizedOrderId = typeof payout?.orderId === 'string' ? payout.orderId.trim() : '';
                                                const payoutErrorLabel = formatPayoutErrorState(payout?.payoutErrorState);
                                                const isBlockingAllowedByState = canBlockPayoutByState(payout?.state);
                                                const canChangePayoutBlockStatus = Boolean(payout?.payoutBlocked) || isBlockingAllowedByState;
                                                const showPayoutPassedHint = !payout?.payoutBlocked && !isBlockingAllowedByState;
                                                const isManualPayoutAllowedByState = canManualPayoutByState(payout?.state);
                                                const isManualPayoutAllowedByTime = canManualPayoutByFinishedAt(payout?.finishedAt);
                                                const manualPayoutLatestAttemptState = normalizedInternalPayoutId
                                                    ? (userManualPayoutLatestAttemptByPayoutId[normalizedInternalPayoutId] || createInitialUserManualPayoutLatestAttemptViewState())
                                                    : createInitialUserManualPayoutLatestAttemptViewState();
                                                const isManualPayoutCooldownActive = isManualPayoutAttemptCooldownActive(
                                                    manualPayoutLatestAttemptState.lastAttemptAt
                                                );
                                                const canManualPayout = isManualPayoutAllowedByState
                                                    && !payout?.payoutBlocked
                                                    && isManualPayoutAllowedByTime
                                                    && !isManualPayoutCooldownActive;
                                                const showManualPayoutBlockedHint = isManualPayoutAllowedByState
                                                    && Boolean(payout?.payoutBlocked);
                                                const showManualPayoutTimeHint = isManualPayoutAllowedByState
                                                    && !payout?.payoutBlocked
                                                    && !isManualPayoutAllowedByTime;
                                                const showManualPayoutCooldownHint = isManualPayoutAllowedByState
                                                    && !payout?.payoutBlocked
                                                    && isManualPayoutAllowedByTime
                                                    && isManualPayoutCooldownActive;
                                                const payoutHistoryState = normalizedInternalPayoutId
                                                    ? (userPayoutBlockHistoryByPayoutId[normalizedInternalPayoutId] || createInitialUserPayoutBlockHistoryViewState())
                                                    : null;
                                                const manualPayoutHistoryState = normalizedInternalPayoutId
                                                    ? (userManualPayoutHistoryByPayoutId[normalizedInternalPayoutId] || createInitialUserManualPayoutHistoryViewState())
                                                    : null;
                                                const displayedManualPayoutHistoryItems = manualPayoutHistoryState?.items?.length
                                                    ? manualPayoutHistoryState.items
                                                    : (manualPayoutLatestAttemptState.latestAttemptItem
                                                        ? [manualPayoutLatestAttemptState.latestAttemptItem]
                                                        : []);
                                                const payoutKey = normalizedInternalPayoutId
                                                    ? normalizedInternalPayoutId
                                                    : (normalizedPayoutId
                                                        ? normalizedPayoutId
                                                        : `${normalizedOrderId}-${index}-${payout?.finishedAt || payout?.paidAt || ''}`);

                                                return (
                                                    <Box
                                                        key={payoutKey}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 2,
                                                            p: 1.25,
                                                            display: 'grid',
                                                            gap: 0.75
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                                <Chip label={formatPayoutState(payout.state)} size="small" variant="outlined" />
                                                                <Chip
                                                                    label={payout?.payoutBlocked ? 'Выплата заблокирована' : 'Выплата не заблокирована'}
                                                                    size="small"
                                                                    color={payout?.payoutBlocked ? 'warning' : 'success'}
                                                                    variant={payout?.payoutBlocked ? 'filled' : 'outlined'}
                                                                />
                                                            </Box>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                                ypmnId: {formatValue(normalizedPayoutId || undefined)}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                            ID выплаты: {formatValue(normalizedInternalPayoutId || undefined)}
                                                        </Typography>

                                                        <Typography fontWeight={700}>
                                                            Сумма выплаты: {formatPrice(Number(payout?.amountSeller))}
                                                        </Typography>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                ID заказа:
                                                            </Typography>
                                                            {normalizedOrderId ? (
                                                                <Box
                                                                    component="button"
                                                                    type="button"
                                                                    onClick={() => handleOpenOrderInNewTab(normalizedOrderId)}
                                                                    sx={{
                                                                        border: 0,
                                                                        background: 'transparent',
                                                                        p: 0,
                                                                        m: 0,
                                                                        color: 'primary.main',
                                                                        cursor: 'pointer',
                                                                        textDecoration: 'underline',
                                                                        textDecorationStyle: 'dotted',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '0.875rem',
                                                                        '&:hover': { color: 'primary.dark' }
                                                                    }}
                                                                >
                                                                    {normalizedOrderId}
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Не указан
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            Название айтема: {formatValue(payout?.adTitleInfo || undefined)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Дата окончания заказа: {formatDateTime(payout?.finishedAt)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Дата выплаты: {formatDateTime(payout?.paidAt)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Метод выплаты: {formatValue(payout?.payoutMethod || undefined)}
                                                        </Typography>

                                                        {payoutErrorLabel && (
                                                            <Typography variant="body2" color="error.main">
                                                                Ошибка выплаты: {payoutErrorLabel}
                                                            </Typography>
                                                        )}

                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                                                            {canManageManualPayout && isManualPayoutAllowedByState && (
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => handleOpenManualPayoutDialog(payout)}
                                                                    disabled={manualPayoutDialogSubmitting || !canManualPayout}
                                                                >
                                                                    Выплатить вручную
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color={payout?.payoutBlocked ? 'warning' : 'error'}
                                                                onClick={() => handleOpenPayoutBlockDialog(payout)}
                                                                disabled={payoutBlockDialogSubmitting || !canChangePayoutBlockStatus}
                                                            >
                                                                {payout?.payoutBlocked ? 'Разблокировать выплату' : 'Заблокировать выплату'}
                                                            </Button>
                                                        </Box>
                                                        {showPayoutPassedHint && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ justifySelf: 'end' }}>
                                                                Нельзя заблокировать выплату, которая уже прошла
                                                            </Typography>
                                                        )}
                                                        {canManageManualPayout && showManualPayoutBlockedHint && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ justifySelf: 'end' }}>
                                                                Ручная выплата недоступна: выплата заблокирована
                                                            </Typography>
                                                        )}
                                                        {canManageManualPayout && showManualPayoutTimeHint && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ justifySelf: 'end' }}>
                                                                Ручная выплата доступна спустя 24 часа после окончания заказа
                                                            </Typography>
                                                        )}
                                                        {canManageManualPayout && showManualPayoutCooldownHint && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ justifySelf: 'end' }}>
                                                                Ручная выплата недоступна: после последней попытки должно пройти 15 минут
                                                            </Typography>
                                                        )}

                                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                            <Box
                                                                component="button"
                                                                type="button"
                                                                onClick={() => handleToggleUserPayoutBlockHistory(normalizedInternalPayoutId)}
                                                                disabled={!normalizedInternalPayoutId}
                                                                sx={{
                                                                    width: '100%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    border: 0,
                                                                    background: 'transparent',
                                                                    p: 1,
                                                                    m: 0,
                                                                    textAlign: 'left',
                                                                    cursor: normalizedInternalPayoutId ? 'pointer' : 'not-allowed',
                                                                    opacity: normalizedInternalPayoutId ? 1 : 0.6
                                                                }}
                                                            >
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    История блокировки/разблокировки
                                                                </Typography>
                                                                {payoutHistoryState?.expanded
                                                                    ? <ExpandLessIcon fontSize="small" />
                                                                    : <ExpandMoreIcon fontSize="small" />}
                                                            </Box>

                                                            {payoutHistoryState?.expanded && (
                                                                <Box sx={{ px: 1, pb: 1 }}>
                                                                    {payoutHistoryState.loading ? (
                                                                        <Typography color="text.secondary">Загрузка истории...</Typography>
                                                                    ) : payoutHistoryState.error ? (
                                                                        <Typography color="error">{payoutHistoryState.error}</Typography>
                                                                    ) : payoutHistoryState.items.length === 0 ? (
                                                                        <Typography color="text.secondary">История изменений отсутствует</Typography>
                                                                    ) : (
                                                                        <Box sx={{ display: 'grid', gap: 0.75 }}>
                                                                            {payoutHistoryState.items.map((historyItem) => {
                                                                                const moderatorName = formatValue(
                                                                                    historyItem?.moderator?.name || historyItem?.moderator?.login || undefined
                                                                                );
                                                                                return (
                                                                                    <Box
                                                                                        key={historyItem.id || `${historyItem.createdAt || ''}-${historyItem.comment}`}
                                                                                        sx={{
                                                                                            border: '1px solid',
                                                                                            borderColor: 'divider',
                                                                                            borderRadius: 1.5,
                                                                                            p: 1,
                                                                                            display: 'grid',
                                                                                            gap: 0.5
                                                                                        }}
                                                                                    >
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                                                            <Chip
                                                                                                label={historyItem.blocked ? 'Блокировка' : 'Разблокировка'}
                                                                                                size="small"
                                                                                                color={historyItem.blocked ? 'error' : 'warning'}
                                                                                            />
                                                                                            <Typography variant="body2" color="text.secondary">
                                                                                                {formatDateTime(historyItem.createdAt)}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                                            {formatValue(historyItem.comment || undefined)}
                                                                                        </Typography>
                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                            Модератор: {moderatorName}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                );
                                                                            })}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        {canManageManualPayout && (
                                                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                                <Box
                                                                    component="button"
                                                                    type="button"
                                                                    onClick={() => handleToggleUserManualPayoutHistory({
                                                                        payoutId: normalizedInternalPayoutId,
                                                                        orderId: normalizedOrderId,
                                                                        ypmnId: normalizedPayoutId
                                                                    })}
                                                                    disabled={!normalizedInternalPayoutId}
                                                                    sx={{
                                                                        width: '100%',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        border: 0,
                                                                        background: 'transparent',
                                                                        p: 1,
                                                                        m: 0,
                                                                        textAlign: 'left',
                                                                        cursor: normalizedInternalPayoutId ? 'pointer' : 'not-allowed',
                                                                        opacity: normalizedInternalPayoutId ? 1 : 0.6
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" fontWeight={600}>
                                                                        История ручных выплат
                                                                    </Typography>
                                                                    {manualPayoutHistoryState?.expanded
                                                                        ? <ExpandLessIcon fontSize="small" />
                                                                        : <ExpandMoreIcon fontSize="small" />}
                                                                </Box>

                                                                {manualPayoutHistoryState?.expanded && (
                                                                    <Box sx={{ px: 1, pb: 1 }}>
                                                                        {manualPayoutHistoryState.loading ? (
                                                                            <Typography color="text.secondary">Загрузка истории...</Typography>
                                                                        ) : manualPayoutHistoryState.error ? (
                                                                            <Typography color="error">{manualPayoutHistoryState.error}</Typography>
                                                                        ) : displayedManualPayoutHistoryItems.length === 0 ? (
                                                                            <Typography color="text.secondary">История ручных выплат отсутствует</Typography>
                                                                        ) : (
                                                                            <Box sx={{ display: 'grid', gap: 0.75 }}>
                                                                                {displayedManualPayoutHistoryItems.map((historyItem) => {
                                                                                    const moderatorName = formatValue(
                                                                                        historyItem?.moderator?.name || historyItem?.moderator?.login || undefined
                                                                                    );
                                                                                    const historyOrderId = typeof historyItem?.orderId === 'string'
                                                                                        ? historyItem.orderId.trim()
                                                                                        : '';
                                                                                    return (
                                                                                        <Box
                                                                                            key={historyItem.id || `${historyItem.createdAt || ''}-${historyItem.success}-${historyItem.errorMessage || ''}`}
                                                                                            sx={{
                                                                                                border: '1px solid',
                                                                                                borderColor: 'divider',
                                                                                                borderRadius: 1.5,
                                                                                                p: 1,
                                                                                                display: 'grid',
                                                                                                gap: 0.5
                                                                                            }}
                                                                                        >
                                                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                                                                <Chip
                                                                                                    label={historyItem.success ? 'Успешно' : 'Ошибка'}
                                                                                                    size="small"
                                                                                                    color={historyItem.success ? 'success' : 'error'}
                                                                                                />
                                                                                                <Typography variant="body2" color="text.secondary">
                                                                                                    {formatDateTime(historyItem.createdAt)}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                            <Typography variant="body2" color="text.secondary">
                                                                                                ID заказа: {formatValue(historyOrderId || undefined)}
                                                                                            </Typography>
                                                                                            {!historyItem.success && (
                                                                                                <Typography variant="body2" color="error.main" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                                                    Ошибка: {formatValue(historyItem.errorMessage || undefined)}
                                                                                                </Typography>
                                                                                            )}
                                                                                            <Typography variant="body2" color="text.secondary">
                                                                                                Модератор: {moderatorName}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    );
                                                                                })}
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    {userPayoutsState.cursor && (
                                        <Box sx={{ mt: 2 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={handleLoadMoreUserPayouts}
                                                disabled={userPayoutsState.loadingMore}
                                            >
                                                {userPayoutsState.loadingMore ? 'Загрузка...' : 'Показать еще'}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box
                                component="button"
                                type="button"
                                onClick={handleToggleUserLinkedPaymentMethods}
                                aria-expanded={userLinkedPaymentMethodsExpanded}
                                aria-label={userLinkedPaymentMethodsExpanded ? 'Свернуть привязанные карты пользователя' : 'Развернуть привязанные карты пользователя'}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: 0,
                                    background: 'transparent',
                                    p: 0,
                                    m: 0,
                                    textAlign: 'left',
                                    cursor: 'pointer'
                                }}
                            >
                                <Typography variant="h6">Привязанные карты</Typography>
                                {userLinkedPaymentMethodsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>

                            {userLinkedPaymentMethodsExpanded && (
                                <Box sx={{ mt: 1.5 }}>
                                    {userLinkedPaymentMethodsState.loading ? (
                                        <Typography color="text.secondary">Загрузка привязанных карт...</Typography>
                                    ) : userLinkedPaymentMethodsState.error ? (
                                        <Typography color="error">{userLinkedPaymentMethodsState.error}</Typography>
                                    ) : userLinkedPaymentMethodsState.items.length === 0 ? (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                            Привязанные карты отсутствуют
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.25 }}>
                                            {userLinkedPaymentMethodsState.items.map((method, index) => {
                                                const normalizedMethodId = typeof method?.id === 'string' ? method.id.trim() : '';
                                                const normalizedYpmnPaymentId = typeof method?.ypmnPaymentId === 'string' ? method.ypmnPaymentId.trim() : '';
                                                const methodKey = normalizedMethodId
                                                    ? normalizedMethodId
                                                    : (normalizedYpmnPaymentId || `linked-method-${index}`);
                                                const paymentMethod = method?.paymentMethod;

                                                return (
                                                    <Box
                                                        key={methodKey}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 2,
                                                            p: 1.25,
                                                            display: 'grid',
                                                            gap: 0.75
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                                <Chip
                                                                    label={method?.isPayoutDefaultMethod ? 'По умолчанию для выплат' : 'Не по умолчанию для выплат'}
                                                                    size="small"
                                                                    color={method?.isPayoutDefaultMethod ? 'success' : 'default'}
                                                                    variant={method?.isPayoutDefaultMethod ? 'filled' : 'outlined'}
                                                                />
                                                                <Chip
                                                                    label={`Статус: ${formatValue(method?.status || undefined)}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            </Box>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                                ypmnPaymentId: {formatValue(normalizedYpmnPaymentId || undefined)}
                                                            </Typography>
                                                        </Box>

                                                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                            ID метода: {formatValue(normalizedMethodId || undefined)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Дата привязки: {formatDateTime(method?.linkedDate)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Тип платежной системы: {formatValue(method?.paymentSystemType || undefined)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Тип метода: {formatValue(paymentMethod?.type)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Платежная система: {formatValue(paymentMethod?.paymentSystemName)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Банк-эмитент: {formatValue(paymentMethod?.cardIssuerBank)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Последние 4 цифры: {formatValue(paymentMethod?.lastFourDigits)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Срок действия карты: {formatDateTime(paymentMethod?.expirationDate)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Срок действия токена: {formatDateTime(paymentMethod?.tokenExpirationDate)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {blockHistoryItems.length > 0 && (
                        <Card>
                            <CardContent>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={handleToggleBlockHistory}
                                    aria-expanded={blockHistoryExpanded}
                                    aria-label={blockHistoryExpanded ? 'Свернуть историю блокировки пользователя' : 'Развернуть историю блокировки пользователя'}
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        border: 0,
                                        background: 'transparent',
                                        p: 0,
                                        m: 0,
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Typography variant="h6">История блокировки пользователя</Typography>
                                    {blockHistoryExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </Box>

                                {blockHistoryExpanded && (
                                    <Box sx={{ display: 'grid', gap: 1.25, mt: 1.5 }}>
                                        {blockHistoryItems.map((item) => {
                                            const moderatorName = formatValue(item?.moderator?.name || item?.moderator?.login || undefined);
                                            const moderatorId = formatValue(item?.moderator?.id ?? undefined);
                                            const normalizedAction = typeof item?.action === 'string' ? item.action.trim().toUpperCase() : '';
                                            const actionLabel = normalizedAction === 'UNBLOCK'
                                                ? 'Разблокировка'
                                                : normalizedAction === 'BLOCK'
                                                    ? 'Блокировка'
                                                    : normalizedAction === 'DELETE_SPAM_MESSAGES'
                                                        ? 'Удаление спама'
                                                        : formatValue(item?.action);
                                            const actionColor = normalizedAction === 'UNBLOCK'
                                                ? 'warning'
                                                : normalizedAction === 'BLOCK'
                                                    ? 'error'
                                                    : normalizedAction === 'DELETE_SPAM_MESSAGES'
                                                        ? 'info'
                                                        : 'default';
                                            return (
                                                <Box
                                                    key={item.id || `${item.createdAt || ''}-${item.comment}`}
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 2,
                                                        p: 1.25,
                                                        display: 'grid',
                                                        gap: 0.75
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            label={actionLabel}
                                                            size="small"
                                                            color={actionColor}
                                                        />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {formatDateTime(item.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {formatValue(item.comment)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Модератор: {moderatorName} (ID: {moderatorId})
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    <UserAvatarDeletionHistory userId={record.id} revision={avatarHistoryRevisions[record.id] || 0} />

                    <Box sx={{
                        mt: 4,
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        '& .MuiButton-root': {
                            minWidth: '200px',
                            py: 1.5
                        }
                    }}>
                        <Button
                            variant="contained"
                            color={currentIsBlocked ? 'warning' : 'error'}
                            onClick={handleOpenBlockDialog}
                            startIcon={currentIsBlocked ? <LockOpenIcon /> : <BlockIcon />}
                            disabled={blockDialogSubmitting}
                        >
                            {currentIsBlocked ? 'Разблокировать пользователя' : 'Заблокировать пользователя'}
                        </Button>
                        {currentIsBlocked && canManageSpamMessages && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleOpenSpamMessagesDialog}
                                startIcon={<DeleteOutlineIcon />}
                                disabled={spamMessagesDialogSubmitting}
                            >
                                Удалить спам сообщения
                            </Button>
                        )}
                    </Box>
                        </>
                    )}
                </Box>

                <Dialog open={payoutBlockDialogOpen} onClose={handleClosePayoutBlockDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {payoutBlockTarget?.payoutBlocked ? 'Разблокировать выплату' : 'Заблокировать выплату'}
                    </DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        <Typography variant="body2" color="text.secondary">
                            {payoutBlockTarget?.payoutBlocked
                                ? 'Укажите причину разблокировки выплаты'
                                : 'Укажите причину блокировки выплаты'}
                        </Typography>
                        <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label="Причина"
                            value={payoutBlockDialogReason}
                            onChange={(event) => handlePayoutBlockReasonChange(event.target.value)}
                            inputProps={{ maxLength: USER_PAYOUT_BLOCK_REASON_MAX_LENGTH }}
                            helperText={`${payoutBlockDialogReason.length}/${USER_PAYOUT_BLOCK_REASON_MAX_LENGTH}`}
                        />
                        {payoutBlockDialogError && (
                            <Typography color="error" variant="body2">
                                {payoutBlockDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClosePayoutBlockDialog} disabled={payoutBlockDialogSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color={payoutBlockTarget?.payoutBlocked ? 'warning' : 'error'}
                            onClick={handleSubmitPayoutBlockDialog}
                            disabled={payoutBlockDialogSubmitting || !payoutBlockDialogReason.trim()}
                        >
                            {payoutBlockDialogSubmitting
                                ? 'Сохранение...'
                                : (payoutBlockTarget?.payoutBlocked ? 'Разблокировать' : 'Заблокировать')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {canManageManualPayout && (
                    <Dialog open={manualPayoutDialogOpen} onClose={handleCloseManualPayoutDialog} maxWidth="sm" fullWidth>
                        <DialogTitle>Выплатить вручную</DialogTitle>
                        <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                            <Typography variant="body2" color="text.secondary">
                                Подтвердите ручную выплату для этой записи. Запрос будет отправлен в платежный сервис.
                            </Typography>
                            {manualPayoutTarget && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                    ID выплаты: {manualPayoutTarget.payoutId}
                                </Typography>
                            )}
                            {manualPayoutDialogError && (
                                <Typography color="error" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {manualPayoutDialogError}
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseManualPayoutDialog} disabled={manualPayoutDialogSubmitting}>
                                Отмена
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmitManualPayoutDialog}
                                disabled={manualPayoutDialogSubmitting || !manualPayoutTarget}
                            >
                                {manualPayoutDialogSubmitting ? 'Отправка...' : 'Выплатить вручную'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}

                {canManageBonuses && (
                    <Dialog open={bonusesAccrualConfirmOpen} onClose={handleCloseBonusesAccrualConfirm} maxWidth="xs" fullWidth>
                        <DialogTitle>Подтвердите начисление бонусов</DialogTitle>
                        <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                            <Typography variant="body2" color="text.secondary">
                                Вы действительно хотите начислить пользователю {bonusesAccrualConfirmAmount ?? 0} бонусов?
                            </Typography>
                            {bonusesActionError && (
                                <Typography color="error" variant="body2">
                                    {bonusesActionError}
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseBonusesAccrualConfirm} disabled={bonusesSubmitting}>
                                Отмена
                            </Button>
                            <Button variant="contained" color="success" onClick={handleConfirmAccrualBonuses} disabled={bonusesSubmitting}>
                                {bonusesSubmitting ? 'Выполнение...' : 'Начислить'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}

                <Dialog open={deleteReviewDialogOpen} onClose={handleCloseDeleteReviewDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Удалить отзыв</DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        <Typography variant="body2" color="text.secondary">
                            Укажите комментарий для удаления. Этот комментарий увидит пользователь.
                        </Typography>
                        <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label="Комментарий"
                            value={deleteReviewDialogReason}
                            onChange={(event) => handleDeleteReviewReasonChange(event.target.value)}
                            inputProps={{ maxLength: USER_REVIEW_DELETE_REASON_MAX_LENGTH }}
                            helperText={`${deleteReviewDialogReason.length}/${USER_REVIEW_DELETE_REASON_MAX_LENGTH}`}
                        />
                        {deleteReviewDialogError && (
                            <Typography color="error" variant="body2">
                                {deleteReviewDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDeleteReviewDialog} disabled={deleteReviewDialogSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleSubmitDeleteReview}
                            startIcon={<DeleteOutlineIcon />}
                            disabled={deleteReviewDialogSubmitting || !deleteReviewDialogReason.trim()}
                        >
                            {deleteReviewDialogSubmitting ? 'Удаление...' : 'Удалить'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={deleteReviewReplyDialogOpen} onClose={handleCloseDeleteReviewReplyDialog} maxWidth="xs" fullWidth>
                    <DialogTitle>Удалить ответ продавца</DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        <Typography variant="body2" color="text.secondary">
                            Ответ продавца будет удален без возможности восстановления.
                        </Typography>
                        {deleteReviewReplyDialogError && (
                            <Typography color="error" variant="body2">
                                {deleteReviewReplyDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDeleteReviewReplyDialog} disabled={deleteReviewReplyDialogSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleSubmitDeleteReviewReply}
                            startIcon={<DeleteOutlineIcon />}
                            disabled={deleteReviewReplyDialogSubmitting || !deleteReviewReplyTarget}
                        >
                            {deleteReviewReplyDialogSubmitting ? 'Удаление...' : 'Удалить'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={blockDialogOpen} onClose={handleCloseBlockDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{currentIsBlocked ? 'Разблокировать пользователя' : 'Заблокировать пользователя'}</DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        <Typography variant="body2" color="text.secondary">
                            {currentIsBlocked
                                ? 'Укажите причину разблокировки пользователя (ее не увидит пользователь, используется только для внутренней аналитики)'
                                : 'Укажите причину блокировки пользователя (ее не увидит пользователь, используется только для внутренней аналитики)'}
                        </Typography>
                        <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label="Комментарий"
                            value={blockDialogComment}
                            onChange={(event) => setBlockDialogComment(event.target.value)}
                        />
                        {blockHistoryLoading && (
                            <Typography variant="caption" color="text.secondary">
                                Обновляем историю блокировок...
                            </Typography>
                        )}
                        {blockDialogError && (
                            <Typography color="error" variant="body2">
                                {blockDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseBlockDialog} disabled={blockDialogSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color={currentIsBlocked ? 'warning' : 'error'}
                            onClick={handleSubmitBlockDialog}
                            disabled={blockDialogSubmitting}
                        >
                            {blockDialogSubmitting
                                ? 'Сохранение...'
                                : (currentIsBlocked ? 'Разблокировать' : 'Заблокировать')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={spamMessagesDialogOpen} onClose={handleCloseSpamMessagesDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Удалить спам сообщения</DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        <Typography variant="body2" color="text.secondary">
                            Вы действительно хотите удалить спам-сообщения пользователя? Это действие безвозвратно.
                        </Typography>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={spamMessagesDialogConfirmChecked}
                                    onChange={(event) => {
                                        setSpamMessagesDialogConfirmChecked(event.target.checked);
                                        if (spamMessagesDialogError) {
                                            setSpamMessagesDialogError(null);
                                        }
                                    }}
                                />
                            )}
                            label="Я осознаю, что данные нельзя вернуть"
                        />
                        {spamMessagesDialogError && (
                            <Typography color="error" variant="body2">
                                {spamMessagesDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseSpamMessagesDialog} disabled={spamMessagesDialogSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleSubmitReplaceSpamMessages}
                            startIcon={<DeleteOutlineIcon />}
                            disabled={spamMessagesDialogSubmitting || !spamMessagesDialogConfirmChecked}
                        >
                            {spamMessagesDialogSubmitting ? 'Удаление...' : 'Удалить спам сообщения'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={feeDialogOpen} onClose={handleCloseFeeDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Изменить комиссию</DialogTitle>
                    <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                        <TextField
                            select
                            fullWidth
                            label="Тип комиссии"
                            value={feeDialogType}
                            onChange={(event) => handleFeeTypeChange(event.target.value as SupportedFeeType)}
                        >
                            <MenuItem value="WELCOME_BONUS">WELCOME_BONUS — Установить приветственную комиссию</MenuItem>
                            <MenuItem value="FIXED">FIXED — Установить фиксированную комиссию</MenuItem>
                            <MenuItem value="DYNAMIC">DYNAMIC — Установить динамический рассчет</MenuItem>
                        </TextField>

                        {feeDialogType !== 'DYNAMIC' && (
                            <TextField
                                fullWidth
                                type="number"
                                label="Процент комиссии"
                                value={feeDialogPercent}
                                onChange={(event) => setFeeDialogPercent(event.target.value)}
                                inputProps={{ min: feeDialogType === 'FIXED' ? 1 : 0, max: 100, step: 1 }}
                            />
                        )}

                        {(feeDialogType === 'WELCOME_BONUS' || feeDialogType === 'FIXED') && (
                            <Box sx={{ display: 'grid', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    type="datetime-local"
                                    label={feeDialogType === 'FIXED' ? 'Действует до (опционально)' : 'Действует до'}
                                    value={feeDialogUntilDate}
                                    onChange={(event) => setFeeDialogUntilDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: feeDialogMinDateTime }}
                                    helperText={feeDialogType === 'FIXED' ? 'Оставьте пустым для бессрочной комиссии' : undefined}
                                />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    <Button size="small" variant="outlined" onClick={() => setFeeDialogUntilDate(buildFeePresetDateTime({ days: 7 }))}>
                                        На неделю
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => setFeeDialogUntilDate(buildFeePresetDateTime({ days: 14 }))}>
                                        На две недели
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => setFeeDialogUntilDate(buildFeePresetDateTime({ days: 30 }))}>
                                        На 30 дней
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => setFeeDialogUntilDate(buildFeePresetDateTime({ months: 3 }))}>
                                        На 3 месяца
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => setFeeDialogUntilDate(buildFeePresetDateTime({ months: 6 }))}>
                                        На полгода
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {feeDialogError && (
                            <Typography color="error" variant="body2">
                                {feeDialogError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseFeeDialog} disabled={feeDialogSubmitting}>
                            Отмена
                        </Button>
                        <Button variant="contained" onClick={handleSubmitFeeDialog} disabled={feeDialogSubmitting}>
                            {feeDialogSubmitting ? 'Сохранение...' : 'Сохранить'}
                        </Button>
                    </DialogActions>
                </Dialog>

            </SimpleShowLayout>
        </ShowView>
        </ShowContextProvider>
    );
};
